import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useMsal } from '@azure/msal-react';
import Header from "./components/Header";
import CustomDropdown from "./components/common/CustomDropdown";
import FileUpload from "./components/FileUpload";
import DocumentTextarea from "./components/DocumentTextarea";
import ParseButton from "./components/ParseButton";
import JsonDisplay from "./components/common/JsonDisplay";
import PlaygroundSidebar from "./components/common/PlaygroundSidebar";
import { parseDocumentWithOpenAI } from "./services/openaiService";
import { analyzeDocument } from "./services/azureDocumentService";
import {
  fetchChunkingFinalJson,
  fetchChunkingPageArtifact,
  fetchChunkingPagePdf,
  fetchChunkingReductionReport,
  fetchChunkingTrace,
  fetchSealDetectionStatus,
  getChunkingDocumentStatus,
  listChunkingPages,
  restartChunkingStage,
  uploadChunkingDocument,
} from "./services/chunkingService";
import { McButton } from "@maersk-global/mds-react-wrapper/components-core/mc-button";
import { McCheckbox } from "@maersk-global/mds-react-wrapper/components-core/mc-checkbox";
import { McIcon } from "@maersk-global/mds-react-wrapper/components-core/mc-icon";
import { McLoadingIndicator } from "@maersk-global/mds-react-wrapper/components-core/mc-loading-indicator";
import { McModal } from "@maersk-global/mds-react-wrapper/components-core/mc-modal";
import { McSegmentedControl } from "@maersk-global/mds-react-wrapper/components-core/mc-segmented-control";
import { McSegmentedControlItem } from "@maersk-global/mds-react-wrapper/components-core/mc-segmented-control-item";
import { McNotification } from "@maersk-global/mds-react-wrapper/components-core/mc-notification";
import { McTab } from "@maersk-global/mds-react-wrapper/components-core/mc-tab";
import { McTabBar } from "@maersk-global/mds-react-wrapper/components-core/mc-tab-bar";
import { McTag } from "@maersk-global/mds-react-wrapper/components-core/mc-tag";
import { COLORS, SPACING, BORDER_RADIUS, SHADOWS, FONT_FAMILY } from "./constants/theme";
import {
  PLAYGROUND_EXTRACTION_METHODS,
  PLAYGROUND_MODELS,
  PLAYGROUND_REDUCER_MODELS,
  PLAYGROUND_REASONING_MODELS,
  PLAYGROUND_FIXED_SETTINGS_MODELS,
} from "./constants/playgroundOptions";
import api from "./services/api";

// ── Reducer agent reasoning (live) ───────────────────────────────────────────

const ThinkingIndicator = ({ label = 'Thinking' }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '13px', color: COLORS.textSecondary, fontStyle: 'italic' }}>
    <span>{label}</span>
    <span style={{ display: 'inline-flex', gap: 3 }} aria-hidden="true">
      {[0, 1, 2].map((i) => (
        <span key={i} style={{
          width: 5, height: 5, borderRadius: '50%', backgroundColor: COLORS.accent,
          animation: `reducerThinkingDot 1.2s ease-in-out ${i * 0.16}s infinite`,
        }} />
      ))}
    </span>
  </div>
);

const formatTurnMs = (ms) => (typeof ms === 'number' ? `${(ms / 1000).toFixed(1)}s` : null);

const reasoningCardStyle = {
  border: `1px solid ${COLORS.border}`,
  borderRadius: BORDER_RADIUS.lg,
  backgroundColor: COLORS.white,
  padding: SPACING.md,
  boxShadow: SHADOWS.sm,
  display: 'flex',
  flexDirection: 'column',
  gap: SPACING.sm,
};

const reasoningSectionLabelStyle = {
  fontSize: '11px', fontWeight: 700, color: COLORS.textSecondary,
  textTransform: 'uppercase', letterSpacing: '0.4px',
};

// MDS has no code font: JSON/code blocks use the MDS text font.
const codeBlockStyle = {
  margin: 0,
  fontFamily: FONT_FAMILY,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
};

// Small rounded label (status, counters, metadata) on the neutral weak surface.
const metaChipStyle = {
  padding: '8px 10px',
  borderRadius: BORDER_RADIUS.md,
  backgroundColor: COLORS.lightGray,
  fontSize: '12px',
};

const toolbarLabelStyle = { color: COLORS.textSecondary, lineHeight: 1, userSelect: 'none' };

// Joined "dropdown + icon button" control in the config toolbar.
const toolbarGroupStyle = (disabled = false) => ({
  display: 'flex',
  alignItems: 'center',
  height: 38,
  border: `1px solid ${COLORS.border}`,
  borderRadius: BORDER_RADIUS.sm,
  backgroundColor: disabled ? COLORS.lightGray : COLORS.white,
});

const toolbarGroupDividerStyle = { alignSelf: 'stretch', width: 1, backgroundColor: COLORS.border, flexShrink: 0 };

// Model Settings slider: custom track drawn behind a transparent native range input.
const rangeWrapperStyle = {
  position: 'relative',
  width: '100%',
  marginBottom: SPACING.xl,
  height: '20px',
  display: 'flex',
  alignItems: 'center',
};
const rangeTrackStyle = { position: 'absolute', height: '4px', borderRadius: '2px', pointerEvents: 'none' };
const rangeInputStyle = { width: '100%', position: 'relative', zIndex: 2, background: 'none', backgroundColor: 'transparent', WebkitAppearance: 'none', appearance: 'none' };

// Tab-style view switcher (no panels: the caller renders the active view).
const ViewTabs = ({ tabs, value, onChange, fit = 'small' }) => {
  const currentIndex = Math.max(0, tabs.findIndex((tab) => tab.value === value));
  return (
    <McTabBar
      fit={fit}
      currentindex={currentIndex}
      tabchange={(e) => {
        const next = tabs[e.detail];
        if (next && next.value !== value) onChange(next.value);
      }}
    >
      {tabs.map((tab) => (
        <McTab key={tab.value} slot="tab" label={tab.label} icon={tab.icon} />
      ))}
      {/* Content renders outside the bar; empty panels keep mc-tab-bar's tab/panel counts equal. */}
      {tabs.map((tab) => <div key={`${tab.value}-panel`} slot="panel" />)}
    </McTabBar>
  );
};

// Toolbar option checkbox; `title` is shown as a native tooltip on hover.
const ToolbarCheckbox = ({ label, checked, onToggle, disabled = false, title }) => (
  <div title={title} style={{ whiteSpace: 'nowrap', opacity: disabled ? 0.45 : 1, transition: 'opacity 0.2s' }}>
    <McCheckbox
      fit="small"
      label={label}
      checked={checked}
      disabled={disabled}
      change={() => onToggle()}
    />
  </div>
);

const PlaygroundToast = ({ toast }) => (toast.show ? (
  <div role="status" style={{
    position: 'fixed',
    top: '20px',
    right: '20px',
    zIndex: 9999,
    maxWidth: '400px',
    boxShadow: SHADOWS.lg,
    animation: 'slideIn 0.3s ease-out',
  }}>
    <McNotification
      appearance={toast.type === 'success' ? 'success' : 'error'}
      icon={toast.type === 'success' ? 'check-circle' : 'exclamation-octagon'}
      body={toast.message}
    />
  </div>
) : null);

// One agent turn: its thinking (fixed-height, scrollable, starts at the top),
// any message text, and the tool calls it made.
const ReasoningTurnCard = ({ turn, toolsRunning }) => {
  const meta = [
    formatTurnMs(turn.llm_ms),
    turn.input_tokens || turn.output_tokens ? `${turn.input_tokens || 0} in / ${turn.output_tokens || 0} out` : null,
  ].filter(Boolean).join(' • ');
  const toolCalls = Array.isArray(turn.tool_calls) ? turn.tool_calls : [];

  return (
    <div data-reasoning-card style={reasoningCardStyle}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: SPACING.sm }}>
        <span style={{ fontSize: '14px', fontWeight: 700, color: COLORS.text }}>Turn {turn.iteration}</span>
        {meta && <span style={{ fontSize: '12px', color: COLORS.textSecondary }}>{meta}</span>}
      </div>

      <div style={reasoningSectionLabelStyle}>Thinking</div>
      {turn.assistant_reasoning ? (
        <div style={{
          height: 240, overflowY: 'auto',
          border: `1px solid ${COLORS.borderLight}`, borderRadius: BORDER_RADIUS.md,
          backgroundColor: COLORS.lightGray, padding: SPACING.sm,
          fontSize: '13px', lineHeight: 1.6, color: COLORS.text,
          whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        }}>
          {turn.assistant_reasoning}
        </div>
      ) : (
        <div style={{ fontSize: '13px', color: COLORS.gray }}>This model returned no reasoning text for this turn.</div>
      )}

      {turn.assistant_content && (
        <>
          <div style={reasoningSectionLabelStyle}>Response</div>
          <div style={{ fontSize: '13px', lineHeight: 1.6, color: COLORS.text, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {turn.assistant_content}
          </div>
        </>
      )}

      {toolCalls.length > 0 && (
        <>
          <div style={reasoningSectionLabelStyle}>Tool calls ({toolCalls.length})</div>
          {toolCalls.map((toolCall, index) => (
            <details key={`${toolCall.name}-${index}`} style={{ border: `1px solid ${COLORS.borderLight}`, borderRadius: BORDER_RADIUS.md, padding: SPACING.sm, backgroundColor: COLORS.lightGray }}>
              <summary style={{ cursor: 'pointer', fontSize: '13px', fontWeight: 600, color: COLORS.text, display: 'flex', alignItems: 'center', gap: 8 }}>
                <code style={{ fontSize: '12px', fontFamily: FONT_FAMILY }}>{toolCall.name}</code>
                {toolsRunning && <ThinkingIndicator label="Running" />}
              </summary>
              <div style={{ marginTop: SPACING.sm, display: 'flex', flexDirection: 'column', gap: SPACING.sm }}>
                <div style={reasoningSectionLabelStyle}>Arguments</div>
                <pre style={{ ...codeBlockStyle, padding: SPACING.sm, backgroundColor: COLORS.white, borderRadius: BORDER_RADIUS.sm, border: `1px solid ${COLORS.border}`, maxHeight: 240, overflow: 'auto', fontSize: '12px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {JSON.stringify(toolCall.arguments || {}, null, 2)}
                </pre>
                {!toolsRunning && (
                  <>
                    <div style={reasoningSectionLabelStyle}>Result</div>
                    <div style={{ fontSize: '12px', color: COLORS.textSecondary, wordBreak: 'break-word' }}>
                      {toolCall.result_summary || 'No result returned.'}
                    </div>
                  </>
                )}
              </div>
            </details>
          ))}
        </>
      )}
    </div>
  );
};

export default function OpticusLab() {
  const navigate = useNavigate();
  const { accounts } = useMsal();
  const userId = accounts[0]?.localAccountId || accounts[0]?.homeAccountId || '';
  
  // State
  const [documentText, setDocumentText] = useState("");
  const [keyValues, setKeyValues] = useState({});
  const [originalKeyValues, setOriginalKeyValues] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingPhase, setLoadingPhase] = useState(''); // 'extracting' or 'analyzing'
  const [pdfFile, setPdfFile] = useState(null); // Store file as object URL (PDF/image)
  const [fileType, setFileType] = useState(null); // 'pdf', 'image', or 'text'
  const [viewMode, setViewMode] = useState('text'); // 'text', 'markdown', or 'file'
  const [toast, setToast] = useState({ show: false, message: '', type: '' }); // type: 'success' or 'error'
  const [sessionStats, setSessionStats] = useState({ adi: 0, neurix: 0, total: 0 }); // Track session statistics
  const [copiedJson, setCopiedJson] = useState(false);
  const [chunkingCopiedJson, setChunkingCopiedJson] = useState(false);
  const [serverTimings, setServerTimings] = useState({ extractionMs: null, analysisMs: null });
  const [tokenUsage, setTokenUsage] = useState(null);
  const [parsedChunks, setParsedChunks] = useState([]);

  // System Messages and Response Formats loaded from database
  const [systemMessages, setSystemMessages] = useState({});
  const [responseFormats, setResponseFormats] = useState({});
  const [loadingMessages, setLoadingMessages] = useState(true);
  const [selectedSystemMessage, setSelectedSystemMessage] = useState("");
  const [showFavouritesOnly, setShowFavouritesOnly] = useState(
    () => sessionStorage.getItem("playground_showFavouritesOnly") === "true"
  );
  const [favouriteNames, setFavouriteNames] = useState(new Set());
  // Internal refs — stable across renders, no re-render on change
  const _msgIdMap = useRef({});        // { name -> id }
  const _contentPromises = useRef({}); // { name -> Promise<{message, format} | undefined> }, dedupes/awaits in-flight fetches
  const fileInputRef = useRef(null);
  const [selectedRf, setResponseFormat] = useState("");
  const [selectedModel, setSelectedModel] = useState(
    () => {
      const storedModel = sessionStorage.getItem("playground_model");
      return PLAYGROUND_MODELS.includes(storedModel) ? storedModel : PLAYGROUND_MODELS[0];
    }
  );
  const [temperature, setTemperature] = useState(
    () => parseFloat(sessionStorage.getItem("playground_temperature") ?? "0.5")
  );
  const [reasoningEffort, setReasoningEffort] = useState(
    () => {
      // "none"/"max" were only valid for the removed DeepSeek V4 Flash option.
      const storedEffort = sessionStorage.getItem("playground_reasoningEffort");
      return ["low", "medium", "high"].includes(storedEffort) ? storedEffort : "low";
    }
  );
  const [showModelSettings, setShowModelSettings] = useState(false);
  // A stored method from an older build (e.g. "NEURIX_LAYOUT_V2") is not a name the
  // backend accepts; fall back to the default instead of sending it.
  const storedExtractionMethod = sessionStorage.getItem("playground_extractionMethod");
  const initialExtractionMethod = PLAYGROUND_EXTRACTION_METHODS.includes(storedExtractionMethod)
    ? storedExtractionMethod
    : PLAYGROUND_EXTRACTION_METHODS[0];
  const [extractionMethod, setExtractionMethod] = useState(initialExtractionMethod);
  const [currentFile, setCurrentFile] = useState(null); // Track currently uploaded file for re-extraction
  const previousExtractionMethod = useRef(initialExtractionMethod);
  const [splitPaneWidth, setSplitPaneWidth] = useState(50); // Percentage width of left pane
  const [isDragging, setIsDragging] = useState(false);
  const [onlyOCR, setOnlyOCR] = useState(false); // Only OCR mode - skip LLM parsing
  const [applyZoom, setApplyZoom] = useState(false); // Zoom PDF pages before extraction
  const isZoomEligibleSystemMessage = ["SCTY stuffing box drain extraction", "SCTY scrape down analysis"].includes(selectedSystemMessage);
  const effectiveApplyZoom = isZoomEligibleSystemMessage ? applyZoom : false;
  const [applyHighResOcr, setApplyHighResOcr] = useState(false); // Azure DI ocrHighResolution add-on for dense/small text
  // Only AzureLayoutMarkdownExtractor (Neurix Layout 2.0) wires apply_high_res_ocr through to Azure DI;
  // other extraction methods ignore the flag, so gate visibility/effect on it too.
  const isHighResOcrEligibleSystemMessage = selectedSystemMessage === "SCTY scrape down analysis" && extractionMethod === "Neurix Layout 2.0";
  const effectiveApplyHighResOcr = isHighResOcrEligibleSystemMessage ? applyHighResOcr : false;
  const [enableChunking, setEnableChunking] = useState(false);
  const [enableSealDetection, setEnableSealDetection] = useState(false);
  const [reducerModel, setReducerModel] = useState(() => {
    const saved = sessionStorage.getItem("playground_reducerModel");
    return PLAYGROUND_REDUCER_MODELS.some(m => m.id === saved) ? saved : PLAYGROUND_REDUCER_MODELS[0].id;
  });
  const [extractionView, setExtractionView] = useState('data'); // 'data', 'text', or 'markdown'
  const [dataDisplayMode, setDataDisplayMode] = useState('table'); // 'table' or 'json' within the Extracted Data view
  const [chunkingView, setChunkingView] = useState('page-wise'); // 'page-wise', 'final-json', or 'agent-reasoning'
  const [chunkingPageDetailView, setChunkingPageDetailView] = useState('markdown'); // 'markdown', 'raw', or 'pdf'
  const [chunkingPdfPreviewUrl, setChunkingPdfPreviewUrl] = useState(null);
  const [chunkingPdfPreviewLoading, setChunkingPdfPreviewLoading] = useState(false);
  const [chunkingPdfPreviewError, setChunkingPdfPreviewError] = useState('');
  const [chunkingPdfPreviewNonce, setChunkingPdfPreviewNonce] = useState(0);
  const [chunkingSession, setChunkingSession] = useState({
    docId: null,
    status: 'idle',
    pageCount: 0,
    progress: null,
    pages: [],
    selectedPage: 1,
    pageWindowStart: 1,
    pageDetails: {},
    finalJson: null,
    trace: null,
    reductionReport: null,
    sealStatus: null,
    loading: false,
    error: null,
  });
  const chunkingPollRef = useRef(null);
  // Last polled pipeline status — used to jump to the reasoning view once reduction starts.
  const lastChunkingStatusRef = useRef(null);
  const chunkingRequestIdRef = useRef(0);
  const chunkingPdfLoadedKeyRef = useRef('');
  const fileDropDepthRef = useRef(0);
  const [isFileDropActive, setIsFileDropActive] = useState(false);

  // Auto-switch to markdown view when Neurix Layout 2.0 is active (always returns markdown)
  useEffect(() => {
    if (extractionMethod === 'Neurix Layout 2.0') {
      setViewMode('markdown');
    }
  }, [extractionMethod]);
  
  // Available models mapped to .env endpoints
  const availableModels = PLAYGROUND_MODELS;
  // Chunking always runs OCR with Neurix Layout 2.0; the Model dropdown picks the reducer agent.
  const chunkingOcrMethod = 'Neurix Layout 2.0';
  const reducerModelName = (PLAYGROUND_REDUCER_MODELS.find(m => m.id === reducerModel) || PLAYGROUND_REDUCER_MODELS[0]).name;
  const handleReducerModelChange = (name) => {
    const match = PLAYGROUND_REDUCER_MODELS.find(m => m.name === name);
    if (!match) return;
    setReducerModel(match.id);
    sessionStorage.setItem("playground_reducerModel", match.id);
  };

  // Fetch full content for a single system message and populate state.
  // Safe to call multiple times — dedupes concurrent calls and lets callers
  // await the actual loaded value (fixes a race where parsing could fire
  // before the lazily-loaded system message content had resolved).
  const loadMessageContent = (name) => {
    if (_contentPromises.current[name]) return _contentPromises.current[name];
    const id = _msgIdMap.current[name];
    if (!id) return Promise.resolve(undefined);

    const promise = (async () => {
      try {
        const res = await api.get(`/system-messages/${id}`);
        const msg = res.data;
        let format = msg.response_format;
        if (typeof format === 'string' && format.trim()) {
          try { format = JSON.parse(format); } catch { format = {}; }
        }
        format = format && typeof format === 'object' ? format : {};
        setSystemMessages(prev => ({ ...prev, [name]: msg.system_message }));
        setResponseFormats(prev => ({ ...prev, [name]: format }));
        return { message: msg.system_message, format };
      } catch (e) {
        console.error(`Failed to load content for system message "${name}":`, e);
        delete _contentPromises.current[name]; // allow retry
        return undefined;
      }
    })();

    _contentPromises.current[name] = promise;
    return promise;
  };

  // Load system message names only at mount — O(n) payload with no content columns
  useEffect(() => {
    const loadNames = async () => {
      try {
        const response = await api.get('/system-messages/names');
        const names = response.data?.names || [];

        const messagesObj = {};
        const formatsObj = {};
        names.forEach(({ id, name }) => {
          messagesObj[name] = '';   // placeholder — filled lazily
          formatsObj[name] = {};
          _msgIdMap.current[name] = id;
        });

        setSystemMessages(messagesObj);
        setResponseFormats(formatsObj);

        // Auto-select, then immediately fetch content for that selection
        if (names.length > 0) {
          const saved = sessionStorage.getItem("playground_systemMessage");
          const validSaved = saved && names.some(n => n.name === saved);
          const name = validSaved ? saved : names[0].name;
          setSelectedSystemMessage(name);
          setResponseFormat(name);
          loadMessageContent(name);
        }
      } catch (error) {
        console.error('Error loading system message names:', error);
      } finally {
        setLoadingMessages(false);
      }
    };

    loadNames();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load this user's favourited system message names
  useEffect(() => {
    if (!userId) return;
    api.get(`/system-messages?user_id=${encodeURIComponent(userId)}&favourites=true&limit=1000`)
      .then(res => {
        const names = new Set((res.data?.messages || []).map(m => m.name));
        setFavouriteNames(names);
      })
      .catch(err => console.error('Failed to load favourite names:', err));
  }, [userId]);

  // Handler for system message change
  const handleSystemMessageChange = (value) => {
    setSelectedSystemMessage(value);
    setResponseFormat(value);
    sessionStorage.setItem("playground_systemMessage", value);
    loadMessageContent(value); // no-op if already loaded
  };

  const allSystemMessageNames = Object.keys(systemMessages).sort();
  const visibleSystemMessageNames = (!showFavouritesOnly || favouriteNames.size === 0)
    ? allSystemMessageNames
    : (() => {
      const filtered = allSystemMessageNames.filter((name) => favouriteNames.has(name));
      return filtered.length > 0 ? filtered : allSystemMessageNames;
    })();
  const systemMessageOptions = loadingMessages
    ? ["Loading..."]
    : visibleSystemMessageNames;

  const formatServerTiming = (timingMs) => {
    if (typeof timingMs !== 'number' || Number.isNaN(timingMs)) {
      return '--';
    }

    if (timingMs < 1000) {
      return `${Math.round(timingMs)} ms`;
    }

    return `${(timingMs / 1000).toFixed(2)} s`;
  };

  const formatTokenCount = (value) => {
    if (typeof value !== 'number' || Number.isNaN(value)) {
      return '--';
    }

    return value.toLocaleString();
  };

  const getChunkingStatusLabel = (status) => {
    if (status === 'reduced') return 'Done';
    if (status === 'failed') return 'Failed';
    if (!status) return 'Pending';
    return status
      .split('_')
      .map((token) => token.charAt(0).toUpperCase() + token.slice(1))
      .join(' ');
  };

  const normalizeConfidencePercent = (value) => {
    if (typeof value !== 'number' || Number.isNaN(value)) return null;
    if (value <= 1) return Math.round(value * 100);
    return Math.round(value);
  };

  const tokenUsageTooltip = tokenUsage
    ? `Prompt tokens: ${formatTokenCount(tokenUsage.prompt_tokens)}\nCompletion tokens: ${formatTokenCount(tokenUsage.completion_tokens)}`
    : '';

  const showTokenUsage = Boolean(
    tokenUsage
    && typeof tokenUsage.total_tokens === 'number'
    && selectedModel.startsWith('GPT')
  );

  // Cleanup PDF object URL on component unmount
  useEffect(() => {
    return () => {
      if (pdfFile && typeof pdfFile === 'string' && pdfFile.startsWith('blob:')) {
        URL.revokeObjectURL(pdfFile);
      }
    };
  }, [pdfFile]);

  useEffect(() => {
    return () => {
      if (chunkingPollRef.current) {
        clearTimeout(chunkingPollRef.current);
      }
    };
  }, []);

  // Re-analyze document ONLY when extraction method changes (not on initial file upload)
  useEffect(() => {
    const reAnalyzeWithNewMethod = async () => {
      if (!currentFile) return; // No file to re-analyze
      
      // Only re-analyze if the extraction method actually changed, not on initial file set
      if (previousExtractionMethod.current === extractionMethod) return;
      
      try {
        setLoading(true);
        setLoadingPhase('extracting');
        setServerTimings(prev => ({ ...prev, extractionMs: null, analysisMs: null }));
        
        const analyzeResult = await analyzeDocument(currentFile, extractionMethod, effectiveApplyZoom, effectiveApplyHighResOcr);
        setServerTimings(prev => ({ ...prev, extractionMs: analyzeResult?.serverTimingMs ?? null }));
        setDocumentText(analyzeResult.content || analyzeResult);
        const usedADI = analyzeResult.route_decision === 'fallback' || analyzeResult.fallback === 'azure-di';
        setSessionStats(prev => ({
          adi: prev.adi + (usedADI ? 1 : 0),
          neurix: prev.neurix + (usedADI ? 0 : 1),
          total: prev.total + 1
        }));
        showToast(`Document re-extracted with ${extractionMethod} method!`, "success");
        
        // Auto-parse if not in Only OCR mode
        if (!onlyOCR) {
          await parseDocument(analyzeResult.content || analyzeResult);
        }
      } catch (err) {
        console.error(err);
        showToast("Failed to re-extract document with new method.", "error");
      } finally {
        setLoading(false);
        setLoadingPhase('');
      }
    };

    reAnalyzeWithNewMethod();
    previousExtractionMethod.current = extractionMethod; // Update the ref after handling
  }, [extractionMethod]);

  const processUploadedFile = async (file) => {
    if (!file) return;
  
    // Reset everything
    setDocumentText("");
    setKeyValues({});
    setOriginalKeyValues({});
    setServerTimings({ extractionMs: null, analysisMs: null });
    setTokenUsage(null);
    setParsedChunks([]);
    setCurrentFile(null); // Clear the current file

    // Clean up previous file URL if exists
    if (pdfFile && typeof pdfFile === 'string' && pdfFile.startsWith('blob:')) {
      URL.revokeObjectURL(pdfFile);
    }
    setPdfFile(null);
    setFileType(null);
    setViewMode('text');
  
    // Check if it's a text file
    if (file.type === "text/plain" || file.name.toLowerCase().endsWith('.txt')) {
      try {
        setLoading(true);
        setLoadingPhase('extracting');
        
        // Store text file as object URL for download/view options
        const fileUrl = URL.createObjectURL(file);
        setPdfFile(fileUrl);
        setFileType('text');
        
        const text = await file.text();
        setDocumentText(text);
        showToast(`Text file "${file.name}" loaded successfully!${onlyOCR ? '' : ' Parsing...'}`, "success");
        
        // Auto-parse after loading text file (unless Only OCR mode)
        if (!onlyOCR) {
          await parseDocument(text);
        } else {
          setLoading(false);
          setLoadingPhase('');
        }
        return;
      } catch (err) {
        console.error(err);
        setLoading(false);
        setLoadingPhase('');
        showToast("Failed to read text file.", "error");
        return;
      }
    }

    // Check if it's an Excel file
    if (file.name.toLowerCase().match(/\.(xlsx|xls|xlsm)$/)) {
      try {
        setLoading(true);
        setLoadingPhase('extracting');
        setServerTimings(prev => ({ ...prev, extractionMs: null, analysisMs: null }));
        
        // Store Excel file name for reference
        setPdfFile(file.name);
        setFileType('excel');
        setCurrentFile(file); // Store file for re-extraction when method changes
        
        const analyzeResult = await analyzeDocument(file, extractionMethod, effectiveApplyZoom, effectiveApplyHighResOcr);
        setServerTimings(prev => ({ ...prev, extractionMs: analyzeResult?.serverTimingMs ?? null }));
        const textContent = analyzeResult.content || analyzeResult;
        setDocumentText(textContent);

        // Excel files are not ADI processed in the context of fallback
        setSessionStats(prev => ({
          adi: prev.adi,
          neurix: prev.neurix + 1,
          total: prev.total + 1
        }));

        showToast(`Excel file "${file.name}" processed successfully!${onlyOCR ? '' : ' Parsing...'}`, "success");
        
        // Auto-parse after extracting text from Excel (unless Only OCR mode)
        if (!onlyOCR) {
          await parseDocument(textContent);
        } else {
          setLoading(false);
          setLoadingPhase('');
        }
      } catch (err) {
        console.error(err);
        setLoading(false);
        setLoadingPhase('');
        showToast("Failed to process Excel file.", "error");
      }
      return;
    }

    if (enableChunking && file.name.toLowerCase().endsWith('.pdf')) {
      await startChunkingWorkflow(file);
      return;
    }
  
    // Check if it's an image file
    if (file.type.startsWith('image/')) {
      try {
        setLoading(true);
        setLoadingPhase('extracting');
        setServerTimings(prev => ({ ...prev, extractionMs: null, analysisMs: null }));
        
        // Store image as object URL for viewing
        const imageUrl = URL.createObjectURL(file);
        setPdfFile(imageUrl);
        setFileType('image');
        setCurrentFile(file); // Store file for re-extraction when method changes
        
        const analyzeResult = await analyzeDocument(file, extractionMethod, effectiveApplyZoom, effectiveApplyHighResOcr);
        setServerTimings(prev => ({ ...prev, extractionMs: analyzeResult?.serverTimingMs ?? null }));
        const textContent = analyzeResult.content || analyzeResult;
        setDocumentText(textContent);

        const usedADI = analyzeResult.route_decision === 'fallback' || analyzeResult.fallback === 'azure-di';
        setSessionStats(prev => ({
          adi: prev.adi + (usedADI ? 1 : 0),
          neurix: prev.neurix + (usedADI ? 0 : 1),
          total: prev.total + 1
        }));

        showToast(`Image "${file.name}" analyzed successfully!${onlyOCR ? '' : ' Parsing...'}`, "success");
        
        // Auto-parse after extracting text from image (unless Only OCR mode)
        if (!onlyOCR) {
          await parseDocument(textContent);
        } else {
          setLoading(false);
          setLoadingPhase('');
        }
      } catch (err) {
        console.error(err);
        setLoading(false);
        setLoadingPhase('');
        showToast("Failed to analyze image.", "error");
      }
      return;
    }
    
    // Handle PDF with Azure Document Intelligence
    try {
      setLoading(true);
      setLoadingPhase('extracting');
      setServerTimings(prev => ({ ...prev, extractionMs: null, analysisMs: null }));
      
      // Store PDF as object URL for viewing (browser memory only)
      const pdfUrl = URL.createObjectURL(file);
      setPdfFile(pdfUrl);
      setFileType('pdf');
      setCurrentFile(file); // Store file for re-extraction when method changes
      
      const analyzeResult = await analyzeDocument(file, extractionMethod, effectiveApplyZoom, effectiveApplyHighResOcr);
      setServerTimings(prev => ({ ...prev, extractionMs: analyzeResult?.serverTimingMs ?? null }));
      const textContent = analyzeResult.content || analyzeResult;
      setDocumentText(textContent);
      const usedADI = analyzeResult.route_decision === 'fallback' || analyzeResult.fallback === 'azure-di';
      setSessionStats(prev => ({
        adi: prev.adi + (usedADI ? 1 : 0),
        neurix: prev.neurix + (usedADI ? 0 : 1),
        total: prev.total + 1
      }));
      showToast(`PDF "${file.name}" analyzed successfully!${onlyOCR ? '' : ' Parsing...'}`, "success");
      
      // Auto-parse after extracting text from PDF (unless Only OCR mode)
      if (!onlyOCR) {
        await parseDocument(textContent);
      } else {
        setLoading(false);
        setLoadingPhase('');
      }
    } catch (err) {
      console.error(err);
      setLoading(false);
      setLoadingPhase('');
      showToast("Failed to analyze document.", "error");
    }
  };

  // File Upload Handler
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    await processUploadedFile(file);
    e.target.value = '';
  };

  const resetFileDropState = () => {
    fileDropDepthRef.current = 0;
    setIsFileDropActive(false);
  };

  const handleFileDragEnter = (e) => {
    if (loading) return;
    e.preventDefault();
    e.stopPropagation();

    fileDropDepthRef.current += 1;
    setIsFileDropActive(true);
  };

  const handleFileDragOver = (e) => {
    if (loading) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleFileDragLeave = (e) => {
    if (loading) return;
    e.preventDefault();
    e.stopPropagation();

    fileDropDepthRef.current = Math.max(0, fileDropDepthRef.current - 1);
    if (fileDropDepthRef.current === 0) {
      setIsFileDropActive(false);
    }
  };

  const handleFileDrop = async (e) => {
    if (loading) return;
    e.preventDefault();
    e.stopPropagation();
    resetFileDropState();

    const droppedFiles = e.dataTransfer?.files;
    if (!droppedFiles || droppedFiles.length === 0) return;

    const file = droppedFiles[0];
    if (enableChunking && !file.name.toLowerCase().endsWith('.pdf')) {
      showToast('Chunking mode supports PDF uploads only.', 'error');
      return;
    }

    await processUploadedFile(file);
  };

  // Show toast notification
  const showToast = (message, type) => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: '', type: '' });
    }, 3000);
  };

  const createEmptyChunkingSession = () => ({
    docId: null,
    status: 'idle',
    pageCount: 0,
    progress: null,
    pages: [],
    selectedPage: 1,
    pageWindowStart: 1,
    pageDetails: {},
    finalJson: null,
    trace: null,
    reductionReport: null,
    sealStatus: null,
    loading: false,
    error: null,
  });

  const resetChunkingSession = () => {
    if (chunkingPollRef.current) {
      clearTimeout(chunkingPollRef.current);
      chunkingPollRef.current = null;
    }

    chunkingRequestIdRef.current += 1;
    setChunkingSession(createEmptyChunkingSession());
    setChunkingView('page-wise');
    lastChunkingStatusRef.current = null;
    setChunkingPageDetailView('markdown');
    setChunkingPdfPreviewUrl(null);
    setChunkingPdfPreviewLoading(false);
    setChunkingPdfPreviewError('');
    setChunkingPdfPreviewNonce(0);
    chunkingPdfLoadedKeyRef.current = '';
    setChunkingCopiedJson(false);
  };

  const loadChunkingPageArtifacts = async (docId, pageNum, requestId) => {
    if (!docId || !pageNum) return;

    const safeRequestId = requestId ?? chunkingRequestIdRef.current;
    let selectedMarkdown = null;
    let selectedExtraction = null;
    let selectedClassification = null;
    let selectedSeal = null;

    setChunkingSession(prev => {
      const existing = prev.pageDetails[pageNum] || {};
      return {
        ...prev,
        pageDetails: {
          ...prev.pageDetails,
          [pageNum]: {
            ...existing,
            loading: true,
            error: null,
          },
        },
      };
    });

    try {
      const fetches = [
        fetchChunkingPageArtifact(docId, pageNum, 'markdown'),
        fetchChunkingPageArtifact(docId, pageNum, 'extraction'),
        fetchChunkingPageArtifact(docId, pageNum, 'classification'),
      ];
      // Seal detection is optional per document -- only fetch it when this
      // upload actually requested it, so pages don't spam 404s when it's off.
      if (enableSealDetection) {
        fetches.push(fetchChunkingPageArtifact(docId, pageNum, 'seal'));
      }
      const [markdownResult, extractionResult, classificationResult, sealResult] =
        await Promise.allSettled(fetches);

      if (safeRequestId !== chunkingRequestIdRef.current) return;

      if (markdownResult.status === 'fulfilled') {
        selectedMarkdown = markdownResult.value;
      }
      if (extractionResult.status === 'fulfilled') {
        selectedExtraction = extractionResult.value;
      }
      if (classificationResult.status === 'fulfilled') {
        selectedClassification = classificationResult.value;
      }
      if (sealResult && sealResult.status === 'fulfilled') {
        selectedSeal = sealResult.value;
      }

      setChunkingSession(prev => {
        const existing = prev.pageDetails[pageNum] || {};
        return {
          ...prev,
          pageDetails: {
            ...prev.pageDetails,
            [pageNum]: {
              ...existing,
              markdown: selectedMarkdown ?? existing.markdown ?? '',
              extraction: selectedExtraction ?? existing.extraction ?? null,
              classification: selectedClassification ?? existing.classification ?? null,
              seal: selectedSeal ?? existing.seal ?? null,
              loading: false,
              error: null,
            },
          },
        };
      });
    } catch (error) {
      if (safeRequestId !== chunkingRequestIdRef.current) return;

      setChunkingSession(prev => {
        const existing = prev.pageDetails[pageNum] || {};
        return {
          ...prev,
          pageDetails: {
            ...prev.pageDetails,
            [pageNum]: {
              ...existing,
              loading: false,
              error: error.message,
            },
          },
        };
      });
    }
  };

  // Schedule the next poll tick. Centralized so both the happy path and the
  // error/backoff path share a single timer slot.
  const scheduleChunkingPoll = (docId, requestId, delay, failureCount = 0) => {
    if (chunkingPollRef.current) {
      clearTimeout(chunkingPollRef.current);
    }
    chunkingPollRef.current = setTimeout(() => {
      syncChunkingSession(docId, requestId, failureCount);
    }, delay);
  };

  // Seal detection is decoupled from the main pipeline's own status -- it may
  // still be mid-flight after the main pipeline reaches 'reduced'/'failed'.
  const isSealStatusTerminal = (sealStatusData) =>
    !sealStatusData || ['complete', 'failed', 'not_requested'].includes(sealStatusData.status);

  const syncChunkingSession = async (docId, requestId = chunkingRequestIdRef.current, failureCount = 0) => {
    try {
      const fetchPromises = [
        getChunkingDocumentStatus(docId),
        listChunkingPages(docId),
      ];
      if (enableSealDetection) {
        fetchPromises.push(fetchSealDetectionStatus(docId));
      }
      const [statusData, pagesData, sealStatusData] = await Promise.all(fetchPromises);
      const resolvedSealStatus = enableSealDetection ? (sealStatusData || null) : null;

      if (requestId !== chunkingRequestIdRef.current) {
        return;
      }

      const pages = Array.isArray(pagesData?.pages) ? pagesData.pages : [];
      const pageCount = typeof statusData?.page_count === 'number'
        ? statusData.page_count
        : (typeof pagesData?.page_count === 'number' ? pagesData.page_count : pages.length);

      setChunkingSession(prev => ({
        ...prev,
        docId,
        status: statusData?.status || prev.status,
        pageCount,
        progress: statusData?.progress || prev.progress,
        pages,
        selectedPage: Math.min(
          Math.max(1, prev.selectedPage || 1),
          Math.max(1, pageCount || pages.length || 1)
        ),
        pageWindowStart: Math.min(
          Math.max(1, prev.pageWindowStart || 1),
          Math.max(1, pageCount || pages.length || 1)
        ),
        sealStatus: resolvedSealStatus ?? prev.sealStatus,
        error: null,
        loading: false,
      }));

      const previousStatus = lastChunkingStatusRef.current;
      lastChunkingStatusRef.current = statusData?.status || previousStatus;

      if (statusData?.status === 'reducing') {
        // Point the right panel at the agent's reasoning when reduction starts.
        if (previousStatus !== 'reducing') {
          setChunkingView('agent-reasoning');
        }
        // The worker rewrites agent_trace.json after every turn while it runs.
        const liveTrace = await fetchChunkingTrace(docId).catch(() => undefined);
        if (requestId !== chunkingRequestIdRef.current) {
          return;
        }
        if (liveTrace !== undefined) {
          // A trace without `live` is left over from an earlier run — don't show it.
          setChunkingSession(prev => ({ ...prev, trace: liveTrace?.live ? liveTrace : null }));
        }
      }

      if (statusData?.status === 'reduced') {
        const [finalResult, traceResult, reductionResult] = await Promise.allSettled([
          fetchChunkingFinalJson(docId),
          fetchChunkingTrace(docId),
          fetchChunkingReductionReport(docId),
        ]);

        if (requestId !== chunkingRequestIdRef.current) {
          return;
        }

        const finalJson = finalResult.status === 'fulfilled' ? finalResult.value : null;
        const trace = traceResult.status === 'fulfilled' ? traceResult.value : null;
        const reductionReport = reductionResult.status === 'fulfilled' ? reductionResult.value : null;

        setChunkingSession(prev => ({
          ...prev,
          status: 'reduced',
          finalJson: finalJson || prev.finalJson,
          trace: trace || prev.trace,
          reductionReport: reductionReport || prev.reductionReport,
          loading: false,
        }));

        const resolvedFinalJson = finalJson || chunkingSession.finalJson;
        if (resolvedFinalJson && typeof resolvedFinalJson === 'object') {
          setKeyValues(resolvedFinalJson);
          setOriginalKeyValues(JSON.parse(JSON.stringify(resolvedFinalJson)));
          setTokenUsage(null);
          setParsedChunks([]);
          setLoading(false);
          setLoadingPhase('');
          // Seal detection runs independently and may still be in flight even
          // though the main pipeline has finished -- keep polling for it alone.
          if (!isSealStatusTerminal(resolvedSealStatus)) {
            scheduleChunkingPoll(docId, requestId, 3000);
          }
          return;
        }

        // The worker is done (status === 'reduced') but the final.json artifact
        // could not be fetched yet (transient API failure). Do NOT strand the
        // UI — keep polling so the result surfaces as soon as it is reachable.
        setChunkingSession(prev => ({
          ...prev,
          error: 'Document finished processing but the final result could not be loaded yet. Retrying…',
          loading: false,
        }));
        setLoading(false);
        setLoadingPhase('');
        scheduleChunkingPoll(docId, requestId, 3000);
        return;
      }

      if (statusData?.status === 'failed') {
        const reductionReport = await fetchChunkingReductionReport(docId).catch(() => null);

        if (requestId !== chunkingRequestIdRef.current) {
          return;
        }

        setChunkingSession(prev => ({
          ...prev,
          status: 'failed',
          reductionReport: reductionReport || prev.reductionReport,
          loading: false,
        }));

        showToast('Chunking workflow failed. Open the reduction report for details.', 'error');
        setLoading(false);
        setLoadingPhase('');
        // The main pipeline failing doesn't affect the independent seal branch.
        if (!isSealStatusTerminal(resolvedSealStatus)) {
          scheduleChunkingPoll(docId, requestId, 3000);
        }
        return;
      }

      // Still processing — keep polling. Reset the failure counter on success.
      scheduleChunkingPoll(docId, requestId, 2500);
    } catch (error) {
      if (requestId !== chunkingRequestIdRef.current) {
        return;
      }

      const nextFailureCount = failureCount + 1;
      // Exponential backoff capped at 15s. A transient status/pages API failure
      // must NOT permanently strand the UI — the worker may have already
      // finished, so we keep retrying until it becomes reachable again.
      const backoff = Math.min(2500 * Math.pow(2, nextFailureCount - 1), 15000);

      setChunkingSession(prev => ({
        ...prev,
        error: `${error.message} — retrying automatically (attempt ${nextFailureCount})…`,
        loading: false,
      }));

      // Release the blocking overlay so the user can interact / manually refresh
      // while background retries continue.
      setLoading(false);
      setLoadingPhase('');

      // Surface a single toast on the first failure to avoid spamming.
      if (nextFailureCount === 1) {
        showToast(`Chunking status refresh failed: ${error.message}. Retrying in the background…`, 'error');
      }

      scheduleChunkingPoll(docId, requestId, backoff, nextFailureCount);
    }
  };

  // Manual recovery: re-attach to the current document and resume polling.
  // Lets the user pull results even if automatic retries were exhausted or the
  // page was left idle — without re-running the whole pipeline.
  const refreshChunkingSession = () => {
    const docId = chunkingSession.docId;
    if (!docId) return;

    if (chunkingPollRef.current) {
      clearTimeout(chunkingPollRef.current);
      chunkingPollRef.current = null;
    }

    chunkingRequestIdRef.current += 1;
    setChunkingSession(prev => ({ ...prev, loading: true, error: null }));
    syncChunkingSession(docId, chunkingRequestIdRef.current);
  };

  const startChunkingWorkflow = async (file) => {
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.pdf')) {
      showToast('Chunking service currently supports PDF uploads only.', 'error');
      return;
    }

    setCurrentFile(file);
    setFileType('pdf');
    setPdfFile(null);
    setDocumentText('');
    setKeyValues({});
    setOriginalKeyValues({});
    setTokenUsage(null);
    setParsedChunks([]);
    setServerTimings({ extractionMs: null, analysisMs: null });

    let systemMessage = systemMessages[selectedSystemMessage];
    let responseFormat = responseFormats[selectedRf];
    if (!systemMessage || !responseFormat) {
      // Lazily-loaded content may not have resolved yet; await it before
      // reporting a false "not selected" error.
      const loaded = await loadMessageContent(selectedSystemMessage);
      systemMessage = systemMessage || loaded?.message;
      // responseFormats placeholders are {} (truthy), so only the name
      // match — not truthiness — tells us the loaded format applies here.
      if (selectedRf === selectedSystemMessage) {
        responseFormat = loaded?.format ?? responseFormat;
      }
    }

    if (!systemMessage || !responseFormat) {
      showToast('Select an instruction and response format before starting chunking.', 'error');
      return;
    }

    resetChunkingSession();
    setLoading(true);
    setLoadingPhase('extracting');
    setChunkingSession(prev => ({ ...prev, loading: true, error: null }));

    const requestId = chunkingRequestIdRef.current;

    try {
      const uploadResult = await uploadChunkingDocument(
        file,
        systemMessage,
        responseFormat,
        enableSealDetection,
        reducerModel
      );

      if (requestId !== chunkingRequestIdRef.current) {
        return;
      }

      const docId = uploadResult.doc_id;
      setChunkingSession(prev => ({
        ...prev,
        docId,
        status: uploadResult.status || 'uploaded',
        loading: true,
        error: null,
      }));
      showToast(`Chunking workflow started for ${file.name}.`, 'success');
      await syncChunkingSession(docId, requestId);
    } catch (error) {
      if (requestId !== chunkingRequestIdRef.current) {
        return;
      }

      setChunkingSession(prev => ({
        ...prev,
        loading: false,
        error: error.message,
      }));
      showToast(`Chunking upload failed: ${error.message}`, 'error');
    } finally {
      if (requestId === chunkingRequestIdRef.current) {
        setLoading(false);
        setLoadingPhase('');
      }
    }
  };

  useEffect(() => {
    if (!enableChunking || !chunkingSession.docId) {
      return;
    }

    const selectedPage = chunkingSession.selectedPage || 1;
    const pageDetails = chunkingSession.pageDetails[selectedPage];

    if (pageDetails?.loading) {
      return;
    }

    const status = chunkingSession.status;
    const canFetchMarkdown = [
      'extracting_markdown',
      'classifying',
      'context_built',
      'extracting',
      'extraction_complete',
      'extracted',
      'reducing',
      'reduced',
      'failed',
    ].includes(status) || (chunkingSession.progress?.pages_markdown_ready ?? 0) > 0;
    const canFetchClassification = [
      'classifying',
      'context_built',
      'extracting',
      'extraction_complete',
      'extracted',
      'reducing',
      'reduced',
      'failed',
    ].includes(status) || (chunkingSession.progress?.pages_classified ?? 0) > 0;
    const canFetchExtraction = [
      'extracting',
      'extraction_complete',
      'extracted',
      'reducing',
      'reduced',
      'failed',
    ].includes(status) || (chunkingSession.progress?.pages_extracted ?? 0) > 0;

    const needsMarkdown = canFetchMarkdown && !pageDetails?.markdown;
    const needsClassification = canFetchClassification && !pageDetails?.classification;
    const needsExtraction = canFetchExtraction && !pageDetails?.extraction;

    if (!needsMarkdown && !needsClassification && !needsExtraction) {
      return;
    }

    loadChunkingPageArtifacts(chunkingSession.docId, selectedPage);
  }, [
    enableChunking,
    chunkingSession.docId,
    chunkingSession.selectedPage,
    chunkingSession.status,
    chunkingSession.progress?.pages_markdown_ready,
    chunkingSession.progress?.pages_classified,
    chunkingSession.progress?.pages_extracted,
  ]);

  useEffect(() => {
    if (!enableChunking || !chunkingSession.docId || chunkingPageDetailView !== 'pdf') {
      return;
    }

    const pages = Array.isArray(chunkingSession.pages) ? chunkingSession.pages : [];
    const totalPages = Math.max(chunkingSession.pageCount || 0, pages.length || 0);
    const splitCount = chunkingSession.progress?.pages_split ?? 0;
    const splitCompleted = (
      (totalPages > 0 && splitCount >= totalPages)
      || [
        'extracting_markdown',
        'classifying',
        'context_built',
        'extracting',
        'extraction_complete',
        'extracted',
        'reducing',
        'reduced',
        'failed',
      ].includes(chunkingSession.status)
    );

    if (!splitCompleted) {
      setChunkingPdfPreviewLoading(false);
      setChunkingPdfPreviewError('PDF preview will be enabled after Split stage completes.');
      return;
    }

    const selectedPage = chunkingSession.selectedPage || 1;
    const expectedPdfKey = `${chunkingSession.docId}:${selectedPage}:${chunkingPdfPreviewNonce}`;

    // Avoid re-fetching the same page PDF on every status/pages poll tick.
    if (
      chunkingPdfLoadedKeyRef.current === expectedPdfKey
      && chunkingPdfPreviewUrl
      && !chunkingPdfPreviewError
    ) {
      return;
    }

    const selectedPageInfo = pages.find(
      (page) => page.page_num === selectedPage
    );

    if (!selectedPageInfo?.available_artifacts?.includes('pdf')) {
      setChunkingPdfPreviewLoading(false);
      setChunkingPdfPreviewError('PDF artifact is not ready for this page yet.');
      return;
    }

    const controller = new AbortController();
    let disposed = false;

    setChunkingPdfPreviewLoading(true);
    setChunkingPdfPreviewError('');

    fetchChunkingPagePdf(chunkingSession.docId, selectedPage, controller.signal)
      .then((blob) => {
        if (disposed) return;
        const objectUrl = URL.createObjectURL(blob);
        setChunkingPdfPreviewUrl((previousUrl) => {
          if (previousUrl) {
            URL.revokeObjectURL(previousUrl);
          }
          return objectUrl;
        });
        chunkingPdfLoadedKeyRef.current = expectedPdfKey;
      })
      .catch((error) => {
        if (disposed || error?.name === 'AbortError') return;
        chunkingPdfLoadedKeyRef.current = '';
        setChunkingPdfPreviewError(error.message || 'Failed to load PDF document.');
      })
      .finally(() => {
        if (!disposed) {
          setChunkingPdfPreviewLoading(false);
        }
      });

    return () => {
      disposed = true;
      controller.abort();
    };
  }, [
    enableChunking,
    chunkingSession.docId,
    chunkingSession.selectedPage,
    chunkingSession.pages,
    chunkingSession.pageCount,
    chunkingSession.progress?.pages_split,
    chunkingSession.status,
    chunkingPageDetailView,
    chunkingPdfPreviewUrl,
    chunkingPdfPreviewError,
    chunkingPdfPreviewNonce,
  ]);

  // Parse document helper function
  const parseDocument = async (text) => {
    if (!text || !text.trim()) {
      showToast("No document text to parse!", "error");
      setLoading(false);
      setLoadingPhase('');
      return;
    }

    setKeyValues({});
    setOriginalKeyValues({});
    setTokenUsage(null);
    setParsedChunks([]);

    try {
      // Switch to analyzing phase
      setLoadingPhase('analyzing');
      setServerTimings(prev => ({ ...prev, analysisMs: null }));
      
      // Lazily-loaded content may not have resolved yet (e.g. parsing
      // triggered right after page load); await it rather than sending blank.
      let systemMessage = systemMessages[selectedSystemMessage];
      let responseFormat = responseFormats[selectedRf];
      if (!systemMessage) {
        const loaded = await loadMessageContent(selectedSystemMessage);
        systemMessage = loaded?.message ?? systemMessages[selectedSystemMessage];
        // responseFormats placeholders are {} (truthy), so only the name
        // match — not truthiness — tells us the loaded format applies here.
        if (selectedRf === selectedSystemMessage) {
          responseFormat = loaded?.format ?? responseFormat;
        }
      }
      
      // Backend expects the display name and maps it to the deployment name itself
      const parsedData = await parseDocumentWithOpenAI(
        text,
        systemMessage,
        responseFormat,
        selectedModel,
        temperature,
        reasoningEffort,
        enableChunking
      );

      setServerTimings(prev => ({ ...prev, analysisMs: parsedData?.serverTimingMs ?? null }));
  setTokenUsage(parsedData?.usage ?? null);
      const chunks = Array.isArray(parsedData?.chunks) ? parsedData.chunks : [];
      setParsedChunks(chunks);

      setKeyValues(parsedData.data);
      setOriginalKeyValues(JSON.parse(JSON.stringify(parsedData.data)));
      
      showToast("Document parsed successfully!", "success");
    } catch (error) {
      console.error("Parsing error:", error);
      setTokenUsage(null);
      showToast(`Failed to parse: ${error.response?.data?.error || error.message}`, "error");
    } finally {
      setLoading(false);
      setLoadingPhase('');
    }
  };

  // Parse with OpenAI Handler (for manual parse button)
  const handleParse = async () => {
    if (enableChunking) {
      showToast('Chunking mode uses the upload workflow instead of manual parsing.', 'error');
      return;
    }

    const textContent = typeof documentText === 'string' ? documentText : (documentText?.content || '');
    if (!textContent.trim()) {
      showToast("No document text to parse!", "error");
      return;
    }

    setLoading(true);
    setLoadingPhase('analyzing');
    await parseDocument(textContent);
  };

  const handleChunkingToggle = async (isEnabled) => {
    if (!isEnabled) {
      setEnableChunking(false);
      // Seal detection can't be on without chunking on -- re-enabling chunking
      // later requires an explicit re-check, so this is deliberately not
      // restored anywhere in the "turning on" path below.
      setEnableSealDetection(false);
      resetChunkingSession();
      return;
    }

    if (onlyOCR) {
      showToast("Chunking mode requires OCR-enabled parsing. Disable Only OCR first.", "error");
      return;
    }

    setEnableChunking(true);

    if (!currentFile || !currentFile.name?.toLowerCase().endsWith('.pdf')) {
      return;
    }

    await startChunkingWorkflow(currentFile);
  };

  // Map stage card key → restart API stage name
  const STAGE_RESTART_MAP = {
    split: 'uploaded',
    markdown: 'split_complete',
    classified: 'markdown_complete',
    extracted: 'classified',
    reduction: 'extracted',
  };

  const [restartingStage, setRestartingStage] = useState(null);

  const handleStageRestart = async (stageKey) => {
    const docId = chunkingSession.docId;
    const apiStage = STAGE_RESTART_MAP[stageKey];
    if (!docId || !apiStage) return;

    setRestartingStage(stageKey);
    try {
      await restartChunkingStage(docId, apiStage);
      showToast(`Stage "${stageKey}" restarted successfully.`, 'success');
      // Resume polling so the UI reflects the new status
      chunkingRequestIdRef.current += 1;
      await syncChunkingSession(docId, chunkingRequestIdRef.current);
    } catch (err) {
      showToast(`Failed to restart stage: ${err.message}`, 'error');
    } finally {
      setRestartingStage(null);
    }
  };

  // Handle split pane dragging
  const handleMouseDown = () => {
    setIsDragging(true);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    
    const container = document.getElementById('split-container');
    if (!container) return;
    
    const rect = container.getBoundingClientRect();
    const newWidth = ((e.clientX - rect.left) / rect.width) * 100;
    
    // Constrain between 20% and 80%
    if (newWidth >= 20 && newWidth <= 80) {
      setSplitPaneWidth(newWidth);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };
    }
  }, [isDragging]);

  const chunkingPages = Array.isArray(chunkingSession.pages) ? chunkingSession.pages : [];
  const chunkingWindowSize = 6;
  const chunkingTotalPages = Math.max(chunkingSession.pageCount || 0, chunkingPages.length || 0);
  const chunkingWindowStart = Math.min(
    Math.max(1, chunkingSession.pageWindowStart || 1),
    Math.max(1, chunkingTotalPages || 1)
  );
  const chunkingVisiblePages = chunkingPages.slice(
    chunkingWindowStart - 1,
    chunkingWindowStart - 1 + chunkingWindowSize
  );
  const chunkingSelectedPage = chunkingSession.selectedPage || 1;
  const chunkingSelectedPageData = chunkingSession.pageDetails[chunkingSelectedPage] || {};
  const chunkingSelectedPageInfo = chunkingPages.find((page) => page.page_num === chunkingSelectedPage) || null;
  const chunkingSelectedMarkdown = typeof chunkingSelectedPageData.markdown === 'string' ? chunkingSelectedPageData.markdown : '';
  const chunkingSelectedExtraction = chunkingSelectedPageData.extraction || null;
  const chunkingSelectedClassification = chunkingSelectedPageData.classification || null;
  const chunkingSelectedSeal = chunkingSelectedPageData.seal || null;
  const chunkingSealStatus = chunkingSession.sealStatus || null;
  const chunkingSealRequested = Boolean(chunkingSealStatus && chunkingSealStatus.status !== 'not_requested');
  // Helper to get page metadata (role, confidence, language) for any page
  const getPageMetadata = (pageNum) => {
    const pageData = chunkingSession.pageDetails[pageNum] || {};
    const pageInfo = chunkingPages.find((p) => p.page_num === pageNum) || {};
    const classification = pageData.classification || pageInfo.classification || {};
    return {
      role: classification.role || pageInfo.role || null,
      confidence: normalizeConfidencePercent(
        classification.confidence ?? pageInfo.confidence ?? null
      ),
      language: classification.language || pageInfo.language || null,
    };
  };

  const chunkingSelectedMeta = getPageMetadata(chunkingSelectedPage);
  const chunkingTraceIterations = Array.isArray(chunkingSession.trace?.iterations) ? chunkingSession.trace.iterations : [];
  // Turn the reducer agent is working on right now (only while reducing).
  const chunkingReducerPending = chunkingSession.status === 'reducing' && chunkingSession.trace?.live ? chunkingSession.trace.pending : null;

  // Agent reasoning follows the run like a chat: the newest turn (or "Thinking…") is
  // scrolled into view as it appears, until the user scrolls up to read an earlier turn.
  const chunkingRightPanelRef = useRef(null);
  const reasoningFollowRef = useRef(true);
  const reasoningProgrammaticScrollUntilRef = useRef(0);
  const reasoningLastScrollTopRef = useRef(0);
  const [reasoningFollowPaused, setReasoningFollowPaused] = useState(false);
  const reasoningFollowKey = `${chunkingTraceIterations.length}:${chunkingReducerPending?.phase || ''}:${chunkingReducerPending?.iteration ?? ''}`;

  const setReasoningFollow = (follow) => {
    reasoningFollowRef.current = follow;
    setReasoningFollowPaused(!follow);
  };

  // The element that actually scrolls the reasoning cards. In the session layout the page
  // root only has a min-height, so it is usually the window rather than the panel itself.
  const getReasoningScroller = () => {
    let node = chunkingRightPanelRef.current;
    while (node && node !== document.body && node !== document.documentElement) {
      const { overflowY } = window.getComputedStyle(node);
      if (/(auto|scroll)/.test(overflowY) && node.scrollHeight > node.clientHeight + 1) return node;
      node = node.parentElement;
    }
    return document.scrollingElement || document.documentElement;
  };

  const isPageScroller = (scroller) => scroller === document.scrollingElement || scroller === document.documentElement;

  const handleReasoningScroll = () => {
    const scroller = getReasoningScroller();
    const scrollTop = scroller.scrollTop;
    const movedUp = scrollTop < reasoningLastScrollTopRef.current - 2;
    reasoningLastScrollTopRef.current = scrollTop;
    if (Date.now() < reasoningProgrammaticScrollUntilRef.current) return;
    const viewHeight = isPageScroller(scroller) ? window.innerHeight : scroller.clientHeight;
    const nearBottom = scroller.scrollHeight - scrollTop - viewHeight < 80;
    if (nearBottom && !reasoningFollowRef.current) setReasoningFollow(true);
    else if (movedUp && reasoningFollowRef.current) setReasoningFollow(false);
  };

  const scrollToLatestReasoningCard = () => {
    const panel = chunkingRightPanelRef.current;
    if (!panel) return;
    const cards = panel.querySelectorAll('[data-reasoning-card]');
    const card = cards[cards.length - 1];
    if (!card) return;
    const scroller = getReasoningScroller();
    const pageScroll = isPageScroller(scroller);
    const viewTop = pageScroll ? 0 : scroller.getBoundingClientRect().top;
    const viewBottom = pageScroll ? window.innerHeight : scroller.getBoundingClientRect().bottom;
    // The panel's sticky tabs header only stays put when the panel itself scrolls.
    const headerHeight = scroller === panel ? (panel.firstElementChild?.getBoundingClientRect().height || 0) : 0;
    const visibleTop = viewTop + headerHeight;
    // Anchor on the newest finished turn: its heading must stay on screen, and the
    // "Thinking…" card below it is shown only as far as that allows.
    const turns = panel.querySelectorAll('[data-reasoning-card]:not([data-reasoning-pending])');
    const anchor = turns[turns.length - 1] || card;
    const lastBox = card.getBoundingClientRect();
    const anchorBox = anchor.getBoundingClientRect();
    const toShowLast = lastBox.bottom - (viewBottom - 16);    // > 0: last card is cut off below
    const toAnchorTop = anchorBox.top - (visibleTop + 12);     // scrolling more than this hides the heading
    let delta;
    if (toShowLast > 0) delta = Math.min(toShowLast, toAnchorTop);
    else if (anchorBox.top < visibleTop) delta = toAnchorTop;  // heading is above the view
    else return;                                               // already in view
    if (Math.abs(delta) < 2) return;
    reasoningProgrammaticScrollUntilRef.current = Date.now() + 800;
    scroller.scrollTo({ top: scroller.scrollTop + delta, behavior: 'smooth' });
  };

  // Listen where the scrolling really happens (window for the page, else the element).
  useEffect(() => {
    if (chunkingView !== 'agent-reasoning' || !chunkingRightPanelRef.current) return;
    const scroller = getReasoningScroller();
    const target = isPageScroller(scroller) ? window : scroller;
    reasoningLastScrollTopRef.current = scroller.scrollTop;
    target.addEventListener('scroll', handleReasoningScroll, { passive: true });
    return () => target.removeEventListener('scroll', handleReasoningScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chunkingView, reasoningFollowKey, chunkingSession.docId]);

  // Start each reasoning view in follow mode.
  useEffect(() => {
    if (chunkingView === 'agent-reasoning') setReasoningFollow(true);
  }, [chunkingView]);

  useEffect(() => {
    if (chunkingView !== 'agent-reasoning' || chunkingSession.status !== 'reducing') return;
    if (!reasoningFollowRef.current) return;
    // Wait for the new card to render before measuring it.
    const frame = requestAnimationFrame(scrollToLatestReasoningCard);
    return () => cancelAnimationFrame(frame);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reasoningFollowKey, chunkingView, chunkingSession.status]);
  const chunkingFinalJson = chunkingSession.finalJson || keyValues;
  const chunkingHasFinalJson = Boolean(chunkingFinalJson && typeof chunkingFinalJson === 'object' && Object.keys(chunkingFinalJson).length > 0);
  const chunkingStatusLabel = getChunkingStatusLabel(chunkingSession.status);
  const chunkingIsProcessing = Boolean(
    chunkingSession.loading
    || loading
    || (chunkingSession.status && !['reduced', 'failed', 'idle'].includes(chunkingSession.status))
  );
  const stageCounts = {
    split: chunkingSession.progress?.pages_split ?? 0,
    markdown: chunkingSession.progress?.pages_markdown_ready ?? 0,
    classified: chunkingSession.progress?.pages_classified ?? 0,
    extracted: chunkingSession.progress?.pages_extracted ?? 0,
  };
  const stageTarget = Math.max(0, chunkingTotalPages || chunkingSession.pageCount || 0);
  const chunkingSplitCompleted = (
    (stageTarget > 0 && stageCounts.split >= stageTarget)
    || [
      'extracting_markdown',
      'classifying',
      'context_built',
      'extracting',
      'extraction_complete',
      'extracted',
      'reducing',
      'reduced',
      'failed',
    ].includes(chunkingSession.status)
  );
  const chunkingCanViewPdf = chunkingSplitCompleted;
  const reductionStageValue = chunkingSession.progress?.reduction_status || (chunkingSession.status === 'reduced' ? 'completed' : 'pending');
  const chunkingStages = [
    {
      key: 'split',
      label: 'Split',
      detail: `${stageCounts.split}/${stageTarget || '--'}`,
      state: chunkingSession.status === 'failed'
        ? 'failed'
        : (stageTarget > 0 && stageCounts.split >= stageTarget ? 'completed' : (chunkingSession.status === 'splitting' || chunkingSession.status === 'uploaded' ? 'running' : 'pending')),
    },
    {
      key: 'markdown',
      label: 'Markdown',
      detail: `${stageCounts.markdown}/${stageTarget || '--'}`,
      state: chunkingSession.status === 'failed'
        ? 'failed'
        : (stageTarget > 0 && stageCounts.markdown >= stageTarget ? 'completed' : (chunkingSession.status === 'extracting_markdown' ? 'running' : 'pending')),
    },
    {
      key: 'classified',
      label: 'Classified',
      detail: `${stageCounts.classified}/${stageTarget || '--'}`,
      state: chunkingSession.status === 'failed'
        ? 'failed'
        : (stageTarget > 0 && stageCounts.classified >= stageTarget ? 'completed' : (chunkingSession.status === 'classifying' ? 'running' : 'pending')),
    },
    {
      key: 'extracted',
      label: 'Extracted',
      detail: `${stageCounts.extracted}/${stageTarget || '--'}`,
      state: chunkingSession.status === 'failed'
        ? 'failed'
        : (stageTarget > 0 && stageCounts.extracted >= stageTarget ? 'completed' : (['context_built', 'extracting', 'extraction_complete', 'extracted'].includes(chunkingSession.status) ? 'running' : 'pending')),
    },
    {
      key: 'reduction',
      label: 'Reduction',
      detail: reductionStageValue,
      state: chunkingSession.status === 'failed'
        ? 'failed'
        : (reductionStageValue === 'completed' || chunkingSession.status === 'reduced'
          ? 'completed'
          : (reductionStageValue === 'running' || chunkingSession.status === 'reducing' ? 'running' : 'pending')),
    },
  ];

  if (enableChunking && chunkingSession.docId) {
    return (
      // Fixed to the viewport: the header and toolbar stay put and only the panels scroll.
      <div style={{
        height: '100vh',
        overflow: 'hidden',
        backgroundColor: COLORS.background,
        display: 'flex',
        flexDirection: 'column'
      }}>
        <Header onClick={() => navigate('/')} />

        <div style={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <PlaygroundSidebar />

          <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, minHeight: 0, overflow: 'hidden' }}>
            {/* Toolbar — unified design (matches main playground) */}
            <div style={{
              backgroundColor: COLORS.background,
              borderBottom: `1px solid ${COLORS.border}`,
              padding: '14px 20px',
              flexShrink: 0,
            }}>
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'flex-end',
                gap: '16px',
              }}>

                {/* System message */}
                <div style={{ flex: '1 1 150px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span className="mds-text--x-small-normal" style={toolbarLabelStyle}>Instruction</span>
                  <div style={toolbarGroupStyle()}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <CustomDropdown
                        hideLabel
                        value={selectedSystemMessage}
                        options={systemMessageOptions}
                        onChange={handleSystemMessageChange}
                        searchable
                        searchPlaceholder="Search by name..."
                        compact
                        triggerStyle={{ border: 'none', borderRadius: 0, minHeight: 38, backgroundColor: 'transparent' }}
                        disabled={loadingMessages || systemMessageOptions.length === 0}
                      />
                    </div>
                    <span style={toolbarGroupDividerStyle} />
                    <McButton
                      label={showFavouritesOnly ? 'Show all instructions' : 'Favourites only'}
                      title={showFavouritesOnly ? 'Show all instructions' : 'Favourites only'}
                      hiddenlabel
                      icon={showFavouritesOnly ? 'star-solid' : 'star'}
                      appearance={showFavouritesOnly ? 'secondary' : 'neutral'}
                      variant="plain"
                      fit="small"
                      click={() => setShowFavouritesOnly(f => { const next = !f; sessionStorage.setItem("playground_showFavouritesOnly", next); return next; })}
                    />
                  </div>
                </div>

                {/* Extraction method (locked while chunking) */}
                <div style={{
                  flex: '1 1 150px', display: 'flex', flexDirection: 'column', gap: 4,
                  opacity: 0.45,
                  transition: 'opacity 0.2s',
                }}
                  title="Locked while chunking is active"
                >
                  <span className="mds-text--x-small-normal" style={toolbarLabelStyle}>Extraction method</span>
                  <CustomDropdown
                    hideLabel
                    value={chunkingOcrMethod}
                    options={[chunkingOcrMethod]}
                    onChange={() => {}}
                    compact
                    triggerStyle={{ minHeight: 38, borderColor: COLORS.border, borderRadius: BORDER_RADIUS.sm }}
                    disabled
                  />
                </div>

                {/* Model (locked while chunking) */}
                <div style={{
                  flex: '1 1 150px', display: 'flex', flexDirection: 'column', gap: 4,
                  opacity: 0.45,
                  transition: 'opacity 0.2s',
                }}
                  title="Locked while chunking is active"
                >
                  <span className="mds-text--x-small-normal" style={toolbarLabelStyle}>Reducer model</span>
                  <div style={toolbarGroupStyle(true)}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <CustomDropdown
                        hideLabel
                        value={reducerModelName}
                        options={[reducerModelName]}
                        onChange={() => {}}
                        compact
                        triggerStyle={{ border: 'none', borderRadius: 0, minHeight: 38, backgroundColor: 'transparent' }}
                        disabled
                      />
                    </div>
                    <span style={toolbarGroupDividerStyle} />
                    <McButton
                      label="Model settings (locked while chunking)"
                      title="Model settings (locked while chunking)"
                      hiddenlabel
                      icon="sliders-horizontal"
                      appearance="neutral"
                      variant="plain"
                      fit="small"
                      disabled
                    />
                  </div>
                </div>

                {/* Divider */}
                <div style={{ width: 1, height: 38, backgroundColor: COLORS.border, flexShrink: 0 }} />

                {/* File picker: native input kept hidden (opened programmatically via ref) */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf"
                  onChange={handleFileUpload}
                  disabled={loading}
                  style={{ display: 'none' }}
                />
                <div
                  onDragEnter={handleFileDragEnter}
                  onDragOver={handleFileDragOver}
                  onDragLeave={handleFileDragLeave}
                  onDrop={handleFileDrop}
                  style={{ borderRadius: BORDER_RADIUS.sm }}
                >
                  <McButton
                    type="button"
                    label={isFileDropActive ? 'Drop file' : 'Choose file'}
                    icon="tray-arrow-up"
                    appearance={isFileDropActive ? 'secondary' : 'neutral'}
                    variant="outlined"
                    disabled={loading}
                    click={() => fileInputRef.current?.click()}
                  />
                </div>

                {/* Only OCR */}
                <ToolbarCheckbox label="Only OCR" checked={onlyOCR} onToggle={() => setOnlyOCR(!onlyOCR)} />

                {/* Zoom PDF */}
                {isZoomEligibleSystemMessage && (
                  <ToolbarCheckbox
                    label="Zoom"
                    title="Zoom PDF pages before extraction to improve OCR/text quality"
                    checked={applyZoom}
                    onToggle={() => setApplyZoom(!applyZoom)}
                  />
                )}

                {/* High-res OCR (Azure DI ocrHighResolution add-on) */}
                {!enableChunking && isHighResOcrEligibleSystemMessage && (
                  <ToolbarCheckbox
                    label="High-res OCR"
                    title="Request Azure DI's high-resolution OCR add-on for small/dense text"
                    checked={applyHighResOcr}
                    onToggle={() => setApplyHighResOcr(!applyHighResOcr)}
                  />
                )}

                {/* Chunking */}
                <ToolbarCheckbox label="Chunking" checked={enableChunking} onToggle={() => handleChunkingToggle(!enableChunking)} />

                {/* Seal & stamp detection — always visible, only checkable while chunking is on */}
                <ToolbarCheckbox
                  label="Seal & stamp detection"
                  title={!enableChunking ? 'Enable Chunking first' : ''}
                  checked={enableSealDetection}
                  disabled={!enableChunking}
                  onToggle={() => setEnableSealDetection(!enableSealDetection)}
                />


                {/* Action */}
                <div style={{ marginLeft: 'auto' }}>
                  {chunkingIsProcessing ? (
                    <McButton label="Chunking in progress…" appearance="primary" variant="filled" loading disabled />
                  ) : chunkingSession.status === 'reduced' ? (
                    <McButton
                      label={restartingStage === 'reduction' ? 'Restarting…' : 'Restart Reduction'}
                      appearance="primary"
                      variant="filled"
                      loading={restartingStage === 'reduction'}
                      disabled={restartingStage === 'reduction'}
                      click={() => handleStageRestart('reduction')}
                    />
                  ) : null}
                </div>

              </div>
            </div>
            <div id="split-container" style={{
              flex: 1,
              minHeight: 0,
              boxSizing: 'border-box',
              padding: SPACING.xl,
              display: 'flex',
              gap: 0,
              alignItems: 'stretch',
              overflow: 'hidden',
              position: 'relative'
            }}>
              <div style={{
                width: `${splitPaneWidth}%`,
                minWidth: 0,
                minHeight: 0,
                boxSizing: 'border-box',
                overflow: 'hidden',
                backgroundColor: COLORS.white,
                borderRadius: BORDER_RADIUS.lg,
                boxShadow: SHADOWS.lg,
                border: `1px solid ${COLORS.border}`,
                padding: SPACING.lg,
                display: 'flex',
                flexDirection: 'column'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: SPACING.md, flexShrink: 0, gap: SPACING.sm }}>
                  <div>
                    <h3 className="mds-headline--x-small" style={{ margin: 0, color: COLORS.text }}>
                      Page Browser
                    </h3>
                    <div className="mds-text--x-small-normal" style={{ marginTop: 4, color: COLORS.textSecondary }}>
                      {chunkingSession.docId}
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', justifyContent: 'flex-end', alignItems: 'center' }}>
                    <McTag fit="small" appearance="neutral-weak" label={`Status: ${chunkingStatusLabel}`} />
                    <McTag fit="small" appearance="neutral-weak" label={`Pages: ${chunkingTotalPages || 0}`} />
                    <McButton
                      label={chunkingSession.loading ? 'Refreshing…' : 'Refresh'}
                      title="Re-fetch status and results from the backend"
                      arialabel="Refresh chunking status"
                      icon="arrow-clockwise"
                      appearance="neutral"
                      variant="outlined"
                      fit="small"
                      loading={chunkingSession.loading}
                      disabled={chunkingSession.loading || !chunkingSession.docId}
                      click={refreshChunkingSession}
                    />
                  </div>
                </div>

                {chunkingSession.error && (
                  <div style={{ marginBottom: SPACING.md, display: 'flex', flexDirection: 'column', gap: SPACING.sm }}>
                    <McNotification
                      appearance="warning"
                      icon="exclamation-triangle"
                      fit="small"
                      verticalalign="middle"
                      body={chunkingSession.error}
                      style={{ wordBreak: 'break-word' }}
                    />
                    <div>
                      <McButton
                        label="Retry now"
                        appearance="neutral"
                        variant="outlined"
                        fit="small"
                        disabled={chunkingSession.loading}
                        click={refreshChunkingSession}
                      />
                    </div>
                  </div>
                )}

                <div style={{ marginBottom: SPACING.md }}>
                  <div className="mds-text--x-small-bold" style={{ color: COLORS.textSecondary, marginBottom: SPACING.xs, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                    Stages
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: SPACING.sm }}>
                    {chunkingStages.map((stage) => {
                      const isRunning = stage.state === 'running';
                      const isCompleted = stage.state === 'completed';
                      const isFailed = stage.state === 'failed';
                      const isRestarting = restartingStage === stage.key;
                      const canRestart = !chunkingIsProcessing && !isRestarting && chunkingSession.docId;
                      return (
                        <div
                          key={stage.key}
                          style={{
                            padding: '8px 10px',
                            borderRadius: BORDER_RADIUS.md,
                            border: `1px solid ${isCompleted ? COLORS.success : isRunning ? COLORS.info : isFailed ? COLORS.errorBorder : COLORS.border}`,
                            backgroundColor: isCompleted ? COLORS.successBg : isRunning ? COLORS.infoBg : isFailed ? COLORS.errorBg : COLORS.lightGray,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '8px',
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                            {isRunning ? (
                              <McLoadingIndicator variant="ring" fit="small" hiddenlabel label="running" />
                            ) : (
                              <span
                                style={{
                                  width: 10,
                                  height: 10,
                                  borderRadius: '50%',
                                  flexShrink: 0,
                                  display: 'inline-block',
                                  backgroundColor: isCompleted ? COLORS.success : isFailed ? COLORS.error : COLORS.gray,
                                }}
                              />
                            )}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
                              <span className="mds-text--x-small-bold" style={{ color: COLORS.text }}>{stage.label}</span>
                              <span className="mds-text--x-small-normal" style={{ color: COLORS.textSecondary }}>
                                {stage.detail} {isRunning ? '• running' : isCompleted ? '• completed' : isFailed ? '• failed' : '• pending'}
                              </span>
                            </div>
                          </div>
                          <McButton
                            label={isRestarting ? 'Restarting...' : 'Restart'}
                            title={canRestart ? `Restart from ${stage.label}` : 'Cannot restart while processing'}
                            arialabel={`Restart from ${stage.label}`}
                            appearance={isFailed ? 'error' : 'neutral'}
                            variant={isFailed ? 'filled' : 'outlined'}
                            fit="small"
                            loading={isRestarting}
                            disabled={!canRestart}
                            click={() => handleStageRestart(stage.key)}
                            style={{ flexShrink: 0 }}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: SPACING.md, gap: SPACING.sm, flexWrap: 'wrap' }}>
                  <div className="mds-text--small-normal" style={{ color: COLORS.textSecondary }}>
                    Showing pages {chunkingWindowStart} - {Math.min(chunkingWindowStart + chunkingWindowSize - 1, chunkingTotalPages || chunkingWindowStart + chunkingWindowSize - 1)}
                  </div>
                  <div style={{ display: 'flex', gap: SPACING.xs }}>
                    <McButton
                      label="Previous"
                      icon="chevron-left"
                      appearance="neutral"
                      variant="outlined"
                      fit="small"
                      disabled={chunkingWindowStart <= 1}
                      click={() => setChunkingSession(prev => ({ ...prev, pageWindowStart: Math.max(1, prev.pageWindowStart - chunkingWindowSize) }))}
                    />
                    <McButton
                      label="Next"
                      trailingicon="chevron-right"
                      appearance="neutral"
                      variant="outlined"
                      fit="small"
                      disabled={chunkingWindowStart + chunkingWindowSize > chunkingTotalPages}
                      click={() => setChunkingSession(prev => ({ ...prev, pageWindowStart: Math.min(Math.max(1, chunkingTotalPages - chunkingWindowSize + 1), prev.pageWindowStart + chunkingWindowSize) }))}
                    />
                  </div>
                </div>

                <div style={{
                  width: '100%',
                  flex: 1,
                  minHeight: 0,
                  overflowY: 'auto',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: SPACING.md,
                  alignContent: 'start'
                }}>
                  {chunkingVisiblePages.length > 0 ? (
                    chunkingVisiblePages.map((page) => {
                      const isSelected = page.page_num === chunkingSelectedPage;
                      const totalSize = Object.values(page.size_bytes || {}).reduce((sum, value) => sum + value, 0);

                      return (
                        <button
                          key={page.page_num}
                          onClick={() => {
                            setChunkingSession(prev => ({
                              ...prev,
                              selectedPage: page.page_num,
                              pageWindowStart: Math.min(
                                Math.max(1, page.page_num - Math.floor(chunkingWindowSize / 2)),
                                Math.max(1, Math.max(1, chunkingTotalPages - chunkingWindowSize + 1))
                              ),
                            }));
                            setChunkingPageDetailView('markdown');
                            if (!chunkingSession.pageDetails[page.page_num] && chunkingSession.docId) {
                              loadChunkingPageArtifacts(chunkingSession.docId, page.page_num);
                            }
                          }}
                          style={{
                            textAlign: 'left',
                            border: `1px solid ${isSelected ? COLORS.primary : COLORS.border}`,
                            borderRadius: BORDER_RADIUS.lg,
                            backgroundColor: isSelected ? COLORS.primaryWeak : COLORS.white,
                            color: COLORS.text,
                            fontFamily: FONT_FAMILY,
                            padding: SPACING.md,
                            boxShadow: isSelected ? SHADOWS.sm : 'none',
                            cursor: 'pointer',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: SPACING.xs,
                            minHeight: 150
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: SPACING.xs }}>
                            <div className="mds-text--medium-bold" style={{ color: COLORS.text }}>
                              Page {page.page_num}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              {isSelected && (
                                <McTag fit="small" appearance="neutral-inverse" label="Selected" />
                              )}
                              {chunkingCanViewPdf && page.available_artifacts?.includes('pdf') && (
                                <McButton
                                  label={`Preview PDF page ${page.page_num}`}
                                  title={`Preview PDF page ${page.page_num}`}
                                  hiddenlabel
                                  icon="eye"
                                  appearance={isSelected && chunkingPageDetailView === 'pdf' ? 'primary' : 'neutral'}
                                  variant={isSelected && chunkingPageDetailView === 'pdf' ? 'filled' : 'plain'}
                                  fit="small"
                                  click={(e) => {
                                    e.stopPropagation();
                                    setChunkingSession(prev => ({
                                      ...prev,
                                      selectedPage: page.page_num,
                                      pageWindowStart: Math.min(
                                        Math.max(1, page.page_num - Math.floor(chunkingWindowSize / 2)),
                                        Math.max(1, Math.max(1, chunkingTotalPages - chunkingWindowSize + 1))
                                      ),
                                    }));
                                    setChunkingPageDetailView('pdf');
                                    if (!chunkingSession.pageDetails[page.page_num] && chunkingSession.docId) {
                                      loadChunkingPageArtifacts(chunkingSession.docId, page.page_num);
                                    }
                                  }}
                                />
                              )}
                            </div>
                          </div>
                          <div className="mds-text--x-small-normal" style={{ color: COLORS.textSecondary }}>
                            {totalSize ? `${Math.round(totalSize / 1024)} KB` : 'Artifact metadata'}
                          </div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                            {(page.available_artifacts || []).map((artifact) => (
                              <McTag key={artifact} fit="small" appearance="neutral-weak" label={artifact} />
                            ))}
                          </div>
                          <div className="mds-text--x-small-normal" style={{ marginTop: 'auto', color: COLORS.textSecondary }}>
                            {chunkingSession.pageDetails[page.page_num]?.loading ? 'Loading page details...' : 'Click to inspect markdown and raw data'}
                          </div>
                        </button>
                      );
                    })
                  ) : (
                    <div style={{
                      gridColumn: '1 / -1',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minHeight: '280px',
                      color: COLORS.gray,
                      fontSize: '14px',
                      textAlign: 'center'
                    }}>
                      {chunkingSession.loading ? 'Chunking workflow is starting...' : 'No page metadata available yet.'}
                    </div>
                  )}
                </div>
              </div>

              <div
                onMouseDown={handleMouseDown}
                style={{
                  width: '8px',
                  cursor: 'col-resize',
                  backgroundColor: isDragging ? COLORS.accent : 'transparent',
                  position: 'relative',
                  flexShrink: 0,
                  transition: isDragging ? 'none' : 'background-color 0.2s ease'
                }}
              >
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '3px',
                  height: '40px',
                  backgroundColor: COLORS.border,
                  borderRadius: '2px'
                }} />
              </div>

              <div ref={chunkingRightPanelRef} style={{
                width: `${100 - splitPaneWidth}%`,
                minWidth: 0,
                minHeight: 0,
                boxSizing: 'border-box',
                backgroundColor: COLORS.white,
                borderRadius: BORDER_RADIUS.lg,
                boxShadow: SHADOWS.lg,
                border: `1px solid ${COLORS.border}`,
                padding: SPACING.lg,
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column'
              }}>
                <div style={{
                  // Sticks flush to the panel's top edge so scrolled content never shows
                  // through the panel padding above the tabs.
                  margin: `-${SPACING.lg}px 0 ${SPACING.md}px`,
                  paddingTop: SPACING.lg,
                  position: 'sticky',
                  top: -SPACING.lg,
                  flexShrink: 0,
                  backgroundColor: COLORS.white,
                  paddingBottom: SPACING.md,
                  borderBottom: `1px solid ${COLORS.borderLight}`,
                  zIndex: 10
                }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: SPACING.sm, marginBottom: SPACING.sm, flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.sm, flexWrap: 'wrap' }}>
                      <h3 className="mds-headline--x-small" style={{ margin: 0, color: COLORS.text }}>
                        Chunking Details
                      </h3>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                        <McTag fit="small" appearance="neutral-weak" label={`Status: ${chunkingStatusLabel}`} />
                        <McTag fit="small" appearance="neutral-weak" label={`Selected Page: ${chunkingSelectedPage}`} />
                      </div>
                    </div>

                    <ViewTabs
                      tabs={[
                        { value: 'page-wise', label: 'Page-wise extracted data' },
                        { value: 'final-json', label: 'Final JSON' },
                        { value: 'agent-reasoning', label: 'Agent reasoning' },
                      ]}
                      value={chunkingView}
                      onChange={setChunkingView}
                    />
                  </div>
                </div>

                {chunkingView === 'page-wise' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING.md, minHeight: 0, flexShrink: 0 }}>
                    {chunkingSealRequested && (
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: SPACING.sm, flexWrap: 'wrap',
                        padding: '8px 10px', borderRadius: BORDER_RADIUS.md,
                        backgroundColor: chunkingSealStatus.status === 'complete' ? COLORS.infoBg : COLORS.lightGray,
                        fontSize: '12px',
                      }}>
                        <strong>Seal &amp; stamp detection:</strong>
                        {chunkingSealStatus.status === 'complete' ? (
                          <span>
                            {chunkingSealStatus.summary?.total_seals_found ?? 0} seal(s) found across{' '}
                            {chunkingSealStatus.summary?.pages_with_seals ?? 0} page(s)
                          </span>
                        ) : chunkingSealStatus.status === 'failed' ? (
                          <span style={{ color: COLORS.error }}>Could not run for this document.</span>
                        ) : (
                          <span>
                            {chunkingSealStatus.status === 'pending' ? 'Starting…' : 'Processing…'}
                            {' '}({(chunkingSealStatus.pages || []).length} of {chunkingSession.pageCount || '?'} pages so far)
                          </span>
                        )}
                      </div>
                    )}
                    <ViewTabs
                      tabs={[
                        { value: 'markdown', label: 'Markdown' },
                        { value: 'raw', label: 'Raw data' },
                        ...(chunkingSession.docId && chunkingCanViewPdf ? [{ value: 'pdf', label: 'PDF', icon: 'eye' }] : []),
                      ]}
                      value={chunkingPageDetailView}
                      onChange={setChunkingPageDetailView}
                    />

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: SPACING.sm }}>
                      <div style={metaChipStyle}>
                        Page {chunkingSelectedPageInfo?.page_num || chunkingSelectedPage}
                      </div>
                      <div style={metaChipStyle}>
                        Role: {chunkingSelectedMeta.role ?? '--'}
                      </div>
                      <div style={metaChipStyle}>
                        Role confidence: {chunkingSelectedMeta.confidence !== null && chunkingSelectedMeta.confidence !== undefined ? `${chunkingSelectedMeta.confidence}%` : '--'}
                      </div>
                      <div style={metaChipStyle}>
                        Language: {chunkingSelectedMeta.language ?? '--'}
                      </div>
                      {chunkingSealRequested && (
                        <div style={{
                          ...metaChipStyle,
                          backgroundColor: chunkingSelectedSeal?.status === 'failed' ? COLORS.errorBg : COLORS.lightGray,
                          color: chunkingSelectedSeal?.status === 'failed' ? COLORS.error : COLORS.text,
                        }}>
                          {!chunkingSelectedSeal
                            ? 'Seal: pending'
                            : chunkingSelectedSeal.status === 'failed'
                            ? `Seal: failed${chunkingSelectedSeal.error ? ` (${chunkingSelectedSeal.error})` : ''}`
                            : chunkingSelectedSeal.seal_present
                            ? `Seal: ${chunkingSelectedSeal.seal_count ?? chunkingSelectedSeal.seals?.length ?? 0} found`
                            : 'Seal: none'}
                        </div>
                      )}
                    </div>

                    {chunkingSealRequested && chunkingSelectedSeal?.status === 'complete' && chunkingSelectedSeal.seal_present && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING.xs }}>
                        {(chunkingSelectedSeal.seals || []).map((seal, idx) => (
                          <div key={idx} style={{
                            padding: '8px 10px', borderRadius: BORDER_RADIUS.md,
                            backgroundColor: COLORS.lightGray, fontSize: '12px',
                            display: 'flex', gap: SPACING.sm, flexWrap: 'wrap',
                          }}>
                            <span><strong>Confidence:</strong> {normalizeConfidencePercent(seal.confidence) !== null ? `${normalizeConfidencePercent(seal.confidence)}%` : '--'}</span>
                            <span><strong>Type:</strong> {seal.seal_type}</span>
                            <span><strong>Shape:</strong> {seal.seal_shape}</span>
                            <span><strong>Location:</strong> {seal.seal_location}</span>
                            <span><strong>Color:</strong> {seal.seal_color ?? '--'}</span>
                            <span><strong>Size:</strong> {seal.size}</span>
                            <span><strong>Clarity:</strong> {seal.text_clarity}</span>
                            <span><strong>Bounded:</strong> {seal.has_defined_boundary ? 'Yes' : 'No'}</span>
                            <span><strong>Signature:</strong> {seal.signature_present ? 'Yes' : 'No'}</span>
                            {seal.authority_label && <span><strong>Authority label:</strong> {seal.authority_label}</span>}
                            {seal.border_description && <span><strong>Border:</strong> {seal.border_description}</span>}
                            {seal.seal_text && <span><strong>Text:</strong> {seal.seal_text}</span>}
                          </div>
                        ))}
                      </div>
                    )}

                    <div style={{ flex: 1, minHeight: 0 }}>
                      {chunkingPageDetailView === 'pdf' ? (
                        chunkingPdfPreviewLoading ? (
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '360px', color: COLORS.gray }}>
                            <McLoadingIndicator label="Loading PDF preview..." />
                          </div>
                        ) : chunkingPdfPreviewError ? (
                          <div style={{
                            border: `1px solid ${COLORS.border}`,
                            borderRadius: BORDER_RADIUS.md,
                            backgroundColor: COLORS.lightGray,
                            minHeight: '520px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: SPACING.sm,
                            color: COLORS.gray,
                            textAlign: 'center',
                            padding: SPACING.xl
                          }}>
                            <div>{chunkingPdfPreviewError}</div>
                            <McButton
                              label="Retry PDF load"
                              appearance="neutral"
                              variant="outlined"
                              fit="small"
                              click={() => setChunkingPdfPreviewNonce((prev) => prev + 1)}
                            />
                          </div>
                        ) : chunkingPdfPreviewUrl ? (
                          <iframe
                            key={`${chunkingSession.docId}-${chunkingSelectedPage}`}
                            src={chunkingPdfPreviewUrl}
                            title={`Page ${chunkingSelectedPage} PDF preview`}
                            style={{
                              width: '100%',
                              height: '520px',
                              border: `1px solid ${COLORS.border}`,
                              borderRadius: BORDER_RADIUS.md,
                              display: 'block',
                            }}
                          />
                        ) : (
                          <div style={{
                            border: `1px solid ${COLORS.border}`,
                            borderRadius: BORDER_RADIUS.md,
                            backgroundColor: COLORS.lightGray,
                            minHeight: '520px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: COLORS.gray,
                            textAlign: 'center',
                            padding: SPACING.xl
                          }}>
                            PDF preview is not available for this page yet.
                          </div>
                        )
                      ) : chunkingSelectedPageData.loading ? (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '360px', color: COLORS.gray }}>
                          <McLoadingIndicator label="Loading page details..." />
                        </div>
                      ) : chunkingPageDetailView === 'markdown' ? (
                        <DocumentTextarea
                          value={chunkingSelectedMarkdown || 'No markdown available for this page yet.'}
                          placeholder="Markdown will appear here"
                          readOnly={true}
                          renderMode="markdown"
                          style={{ minHeight: '520px', height: '100%' }}
                        />
                      ) : (
                        <div style={{
                          border: `1px solid ${COLORS.border}`,
                          borderRadius: BORDER_RADIUS.md,
                          backgroundColor: COLORS.white,
                          padding: SPACING.md,
                          minHeight: '520px',
                          overflow: 'auto'
                        }}>
                          {chunkingSelectedExtraction ? (
                            <JsonDisplay data={chunkingSelectedExtraction} />
                          ) : chunkingSelectedMarkdown ? (
                            <pre style={{ ...codeBlockStyle, fontSize: '12px', lineHeight: 1.6, color: COLORS.text }}>
                              {chunkingSelectedMarkdown}
                            </pre>
                          ) : (
                            <div style={{ color: COLORS.gray, fontSize: '14px' }}>
                              No raw extraction data available for this page yet.
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {chunkingView === 'final-json' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING.md, minHeight: 0, flexShrink: 0 }}>
                    <div style={{ display: 'flex', gap: SPACING.sm, flexWrap: 'wrap', alignItems: 'center' }}>
                      <div style={{ display: 'flex', gap: SPACING.sm, flexWrap: 'wrap' }}>
                        <div style={metaChipStyle}>
                          Status: {chunkingStatusLabel}
                        </div>
                        <div style={metaChipStyle}>
                          Pages processed: {chunkingSession.progress?.pages_extracted ?? 0}
                        </div>
                      </div>
                      {chunkingHasFinalJson && (
                        <McButton
                          label={chunkingCopiedJson ? 'Copied' : 'Copy JSON'}
                          title="Copy final JSON"
                          icon={chunkingCopiedJson ? 'check' : 'copy'}
                          appearance="neutral"
                          variant="outlined"
                          fit="small"
                          click={() => {
                            navigator.clipboard.writeText(JSON.stringify(chunkingFinalJson, null, 2));
                            setChunkingCopiedJson(true);
                            setTimeout(() => setChunkingCopiedJson(false), 2000);
                          }}
                          style={{ marginLeft: 'auto', flexShrink: 0 }}
                        />
                      )}
                    </div>
                    {chunkingHasFinalJson ? (
                      <JsonDisplay data={chunkingFinalJson} />
                    ) : (
                      <div style={{
                        border: `1px solid ${COLORS.border}`,
                        borderRadius: BORDER_RADIUS.md,
                        backgroundColor: COLORS.lightGray,
                        minHeight: '520px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: COLORS.gray,
                        textAlign: 'center',
                        padding: SPACING.xl
                      }}>
                        Final JSON will appear here when the reduction agent completes.
                      </div>
                    )}
                  </div>
                )}

                {chunkingView === 'agent-reasoning' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING.md, minHeight: 0, flexShrink: 0 }}>
                    {chunkingSession.reductionReport?.metrics && (
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
                        gap: SPACING.sm
                      }}>
                        {[
                          ['Mode', chunkingSession.reductionReport.metrics.reduction_mode],
                          ['Model', chunkingSession.reductionReport.metrics.llm_model_used],
                          ['Schema', chunkingSession.reductionReport.metrics.schema_id],
                          ['LLM calls', chunkingSession.reductionReport.metrics.llm_calls],
                          ['Input tokens', chunkingSession.reductionReport.metrics.llm_total_input_tokens],
                          ['Output tokens', chunkingSession.reductionReport.metrics.llm_total_output_tokens],
                          ['Duplicates removed', chunkingSession.reductionReport.metrics.duplicates_removed_count],
                          ['LLM removed', chunkingSession.reductionReport.metrics.llm_removed_count],
                        ].map(([label, value]) => (
                          <div key={label} style={{ border: `1px solid ${COLORS.border}`, borderRadius: BORDER_RADIUS.md, padding: SPACING.md, backgroundColor: COLORS.lightGray }}>
                            <div className="mds-text--x-small-normal" style={{ color: COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.4px' }}>{label}</div>
                            <div className="mds-text--small-bold" style={{ color: COLORS.text, marginTop: '4px' }}>{String(value ?? '--')}</div>
                          </div>
                        ))}
                      </div>
                    )}

                    {(chunkingSession.reductionReport?.guardrail_warnings?.length || chunkingSession.reductionReport?.llm_reduction_notes?.length) && (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: SPACING.sm }}>
                        {chunkingSession.reductionReport?.guardrail_warnings?.length > 0 && (
                          <div style={{ border: `1px solid ${COLORS.border}`, borderRadius: BORDER_RADIUS.md, backgroundColor: COLORS.warningBg, padding: SPACING.md }}>
                            <div className="mds-text--small-bold" style={{ color: COLORS.text, marginBottom: SPACING.sm }}>Guardrail warnings</div>
                            <ul style={{ margin: 0, paddingLeft: '18px', color: COLORS.text, fontSize: '13px', lineHeight: 1.6 }}>
                              {chunkingSession.reductionReport.guardrail_warnings.map((warning, index) => (
                                <li key={`${warning}-${index}`}>{warning}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {chunkingSession.reductionReport?.llm_reduction_notes?.length > 0 && (
                          <div style={{ border: `1px solid ${COLORS.border}`, borderRadius: BORDER_RADIUS.md, backgroundColor: COLORS.background, padding: SPACING.md }}>
                            <div className="mds-text--small-bold" style={{ color: COLORS.text, marginBottom: SPACING.sm }}>Reduction notes</div>
                            <ul style={{ margin: 0, paddingLeft: '18px', color: COLORS.text, fontSize: '13px', lineHeight: 1.6 }}>
                              {chunkingSession.reductionReport.llm_reduction_notes.map((note, index) => (
                                <li key={`${note}-${index}`}>{note}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}

                    <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING.md }}>
                      {chunkingSession.status === 'reducing' && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.sm, fontSize: '13px', color: COLORS.textSecondary }}>
                          <span style={{ fontWeight: 700, color: COLORS.text }}>Reducer agent running</span>
                          {chunkingSession.trace?.model && <span>• {chunkingSession.trace.model}</span>}
                        </div>
                      )}

                      {chunkingTraceIterations.map((turn) => (
                        <ReasoningTurnCard
                          key={turn.iteration}
                          turn={turn}
                          toolsRunning={chunkingReducerPending?.phase === 'running_tools' && chunkingReducerPending.iteration === turn.iteration}
                        />
                      ))}

                      {chunkingSession.status === 'reducing' && (!chunkingReducerPending || chunkingReducerPending.phase !== 'running_tools') && (
                        <div data-reasoning-card data-reasoning-pending style={reasoningCardStyle}>
                          {chunkingReducerPending?.phase === 'thinking' ? (
                            <span style={{ fontSize: '14px', fontWeight: 700, color: COLORS.text }}>Turn {chunkingReducerPending.iteration}</span>
                          ) : null}
                          <ThinkingIndicator label={chunkingReducerPending?.phase === 'thinking' ? 'Thinking' : 'Preparing the reducer'} />
                        </div>
                      )}

                      {chunkingSession.status === 'reducing' && reasoningFollowPaused && (
                        <McButton
                          type="button"
                          label="Latest turn"
                          icon="arrow-down"
                          appearance="primary"
                          variant="filled"
                          fit="small"
                          click={() => { setReasoningFollow(true); scrollToLatestReasoningCard(); }}
                          style={{ position: 'sticky', bottom: SPACING.sm, alignSelf: 'center', zIndex: 2 }}
                        />
                      )}

                      {chunkingSession.status !== 'reducing' && chunkingSession.trace?.final_reasoning && (
                        <div style={reasoningCardStyle}>
                          <div style={reasoningSectionLabelStyle}>
                            {chunkingSession.trace.status === 'fast_path_submitted' ? 'Fast path — no agent turns needed' : 'Final reasoning'}
                          </div>
                          <div style={{ fontSize: '13px', lineHeight: 1.6, color: COLORS.text, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {chunkingSession.trace.final_reasoning}
                          </div>
                        </div>
                      )}

                      {chunkingSession.status !== 'reducing' && chunkingSession.trace?.retrospective && (
                        <div style={reasoningCardStyle}>
                          <div style={reasoningSectionLabelStyle}>Agent feedback — what would have made this easier</div>
                          {chunkingSession.trace.retrospective.feedback?.top_suggestion && (
                            <div style={{ fontSize: '13px', lineHeight: 1.6, color: COLORS.text, fontWeight: 600, marginBottom: SPACING.sm }}>
                              {chunkingSession.trace.retrospective.feedback.top_suggestion}
                            </div>
                          )}
                          <pre style={{ ...codeBlockStyle, fontSize: '12px', lineHeight: 1.5, color: COLORS.text }}>
                            {chunkingSession.trace.retrospective.feedback
                              ? JSON.stringify(chunkingSession.trace.retrospective.feedback, null, 2)
                              : `${chunkingSession.trace.retrospective.error || 'No feedback'}${chunkingSession.trace.retrospective.raw ? `\n\n${chunkingSession.trace.retrospective.raw}` : ''}`}
                          </pre>
                        </div>
                      )}

                      {chunkingSession.status !== 'reducing' && chunkingTraceIterations.length === 0 && !chunkingSession.trace?.final_reasoning && (
                        <div style={{
                          border: `1px solid ${COLORS.border}`,
                          borderRadius: BORDER_RADIUS.md,
                          backgroundColor: COLORS.lightGray,
                          minHeight: '320px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: COLORS.gray,
                          textAlign: 'center',
                          padding: SPACING.xl
                        }}>
                          {chunkingSession.status === 'reduced'
                            ? 'Agent trace is not available yet.'
                            : 'Agent reasoning will appear here once reduction starts.'}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <PlaygroundToast toast={toast} />
      </div>
    );
  }

  return (
    <div style={{ 
      height: "100vh",
      backgroundColor: COLORS.background,
      display: "flex",
      flexDirection: "column",
      overflow: "hidden"
    }}>
      <Header onClick={() => navigate('/')} />

      {/* Page body: sidebar + content */}
      <div style={{ display: "flex", flex: 1, minHeight: 0, overflow: "hidden" }}>
      <PlaygroundSidebar />

      {/* Right: toolbar + main content */}
      <div style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0, minHeight: 0, overflow: "hidden" }}>

      {/* ── Config toolbar ── */}
      <div style={{
        backgroundColor: COLORS.background,
        borderBottom: `1px solid ${COLORS.border}`,
        padding: '14px 20px',
        flexShrink: 0,
      }}>
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-end',
          gap: '16px',
        }}>

          {/* System message */}
          <div style={{ flex: '1 1 150px', display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span className="mds-text--x-small-normal" style={toolbarLabelStyle}>Instruction</span>
            <div style={toolbarGroupStyle()}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <CustomDropdown
                  hideLabel
                  value={selectedSystemMessage}
                  options={systemMessageOptions}
                  onChange={handleSystemMessageChange}
                  searchable
                  searchPlaceholder="Search by name..."
                  compact
                  triggerStyle={{ border: 'none', borderRadius: 0, minHeight: 38, backgroundColor: 'transparent' }}
                  disabled={loadingMessages || systemMessageOptions.length === 0}
                />
              </div>
              <span style={toolbarGroupDividerStyle} />
              <McButton
                label={showFavouritesOnly ? 'Show all instructions' : 'Favourites only'}
                title={showFavouritesOnly ? 'Show all instructions' : 'Favourites only'}
                hiddenlabel
                icon={showFavouritesOnly ? 'star-solid' : 'star'}
                appearance={showFavouritesOnly ? 'secondary' : 'neutral'}
                variant="plain"
                fit="small"
                click={() => setShowFavouritesOnly(f => { const next = !f; sessionStorage.setItem("playground_showFavouritesOnly", next); return next; })}
              />
            </div>
          </div>

          {/* Extraction method */}
          <div style={{
            flex: '1 1 150px', display: 'flex', flexDirection: 'column', gap: 4,
            opacity: enableChunking ? 0.45 : 1,
            transition: 'opacity 0.2s',
          }}
            title={enableChunking ? 'Locked while chunking is active' : ''}
          >
            <span className="mds-text--x-small-normal" style={toolbarLabelStyle}>OCR method</span>
            <CustomDropdown
              hideLabel
              value={enableChunking ? chunkingOcrMethod : extractionMethod}
              options={enableChunking ? [chunkingOcrMethod] : PLAYGROUND_EXTRACTION_METHODS}
              onChange={(v) => { setExtractionMethod(v); sessionStorage.setItem("playground_extractionMethod", v); }}
              compact
              triggerStyle={{ minHeight: 38, borderColor: COLORS.border, borderRadius: BORDER_RADIUS.sm }}
              disabled={enableChunking}
            />
          </div>

          {/* Model — picks the reducer agent while chunking is active */}
          <div style={{
            flex: '1 1 150px', display: 'flex', flexDirection: 'column', gap: 4,
            transition: 'opacity 0.2s',
          }}
            title={enableChunking ? 'Reducer agent used to merge chunk results' : ''}
          >
            <span className="mds-text--x-small-normal" style={toolbarLabelStyle}>{enableChunking ? 'Reducer model' : 'Model'}</span>
            <div style={toolbarGroupStyle()}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <CustomDropdown
                  hideLabel
                  value={enableChunking ? reducerModelName : selectedModel}
                  options={enableChunking ? PLAYGROUND_REDUCER_MODELS.map(m => m.name) : availableModels}
                  onChange={enableChunking
                    ? handleReducerModelChange
                    : (v) => { setSelectedModel(v); sessionStorage.setItem("playground_model", v); }}
                  compact
                  triggerStyle={{ border: 'none', borderRadius: 0, minHeight: 38, backgroundColor: 'transparent' }}
                />
              </div>
              <span style={toolbarGroupDividerStyle} />
              <McButton
                label="Model settings"
                title="Model settings"
                hiddenlabel
                icon="sliders-horizontal"
                appearance="neutral"
                variant="plain"
                fit="small"
                disabled={enableChunking}
                click={() => setShowModelSettings(true)}
              />
            </div>
          </div>

          {/* Divider */}
          <div style={{ width: 1, height: 38, backgroundColor: COLORS.border, flexShrink: 0 }} />

          {/* File upload: native input kept hidden (opened programmatically via ref) */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".txt,.pdf,.xlsx,.xls,.xlsm,.doc,.docx"
            onChange={handleFileUpload}
            disabled={loading}
            style={{ display: 'none' }}
          />
          <div
            onDragEnter={handleFileDragEnter}
            onDragOver={handleFileDragOver}
            onDragLeave={handleFileDragLeave}
            onDrop={handleFileDrop}
            style={{ borderRadius: BORDER_RADIUS.sm }}
          >
            <McButton
              type="button"
              label={isFileDropActive ? '[ ---- Drop file here ---- ]' : 'Choose file'}
              icon="tray-arrow-up"
              appearance={isFileDropActive ? 'secondary' : 'neutral'}
              variant="outlined"
              disabled={loading}
              click={() => fileInputRef.current?.click()}
            />
          </div>

          {/* Only OCR */}
          <ToolbarCheckbox label="Only OCR" checked={onlyOCR} onToggle={() => setOnlyOCR(!onlyOCR)} />

          {/* Zoom PDF */}
          {isZoomEligibleSystemMessage && (
            <ToolbarCheckbox
              label="Zoom"
              title="Zoom PDF pages before extraction to improve OCR/text quality"
              checked={applyZoom}
              onToggle={() => setApplyZoom(!applyZoom)}
            />
          )}

          {/* High-res OCR (Azure DI ocrHighResolution add-on) */}
          {isHighResOcrEligibleSystemMessage && (
            <ToolbarCheckbox
              label="High-res OCR"
              title="Request Azure DI's high-resolution OCR add-on for small/dense text"
              checked={applyHighResOcr}
              onToggle={() => setApplyHighResOcr(!applyHighResOcr)}
            />
          )}

          {/* Chunking */}
          <ToolbarCheckbox label="Chunking" checked={enableChunking} onToggle={() => handleChunkingToggle(!enableChunking)} />

          {/* Seal & stamp detection — always visible, only checkable while chunking is on */}
          <ToolbarCheckbox
            label="Seal & stamp detection"
            title={!enableChunking ? 'Enable Chunking first' : ''}
            checked={enableSealDetection}
            disabled={!enableChunking}
            onToggle={() => setEnableSealDetection(!enableSealDetection)}
          />


          {/* Parse */}
          <div style={{ marginLeft: 'auto' }}>
            <ParseButton
              onClick={handleParse}
              loading={loading}
              loadingPhase={loadingPhase}
              disabled={!(typeof documentText === 'string' ? documentText : (documentText?.content || '')).trim() || loading || onlyOCR || enableChunking}
            />
          </div>

        </div>
      </div>
      {/* Main Content Area - Split Pane View */}
      <div 
        id="split-container"
        style={{ 
          flex: 1,
          minHeight: 0,
          boxSizing: "border-box",
          padding: SPACING.xl,
          display: "flex",
          gap: 0,
          alignItems: "stretch",
          overflow: "hidden",
          position: 'relative'
        }}
      >
        {/* Left Pane - PDF/Image Viewer */}
        <div style={{ 
          width: `${splitPaneWidth}%`,
          minWidth: 0,
          minHeight: 0,
          boxSizing: "border-box",
          overflowX: "hidden",
          overflowY: "auto",
          backgroundColor: COLORS.white,
          borderRadius: BORDER_RADIUS.lg,
          boxShadow: SHADOWS.lg,
          border: `1px solid ${COLORS.border}`,
          padding: SPACING.xl,
          display: "flex",
          flexDirection: "column"
        }}>
          <div style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: SPACING.md,
            flexShrink: 0
          }}>
            <h3 className="mds-headline--x-small" style={{
              margin: 0,
              color: COLORS.text,
            }}>
              Document {fileType === 'pdf' ? 'PDF' : fileType === 'image' ? 'Image' : 'File'}
            </h3>
            
            {extractionMethod === "Neurix Layout 2.0" && (
              <div style={{
                display: "flex",
                gap: "4px",
                alignItems: "center"
              }}>
                <span title="Total ADI processed">
                  <McTag fit="small" appearance="success" label={`ADI: ${sessionStats.adi}`} />
                </span>
                <span title="Total Neurix processed">
                  <McTag fit="small" appearance="error" label={`Neurix: ${sessionStats.neurix}`} />
                </span>
                <span title="Total processed">
                  <McTag fit="small" appearance="neutral-inverse" label={`Total: ${sessionStats.total}`} />
                </span>
              </div>
            )}
          </div>
          
          <div style={{
            width: "100%",
            flex: 1,
            border: `1px solid ${COLORS.border}`,
            borderRadius: BORDER_RADIUS.md,
            overflow: "hidden",
            backgroundColor: COLORS.white
          }}>
            {pdfFile ? (
              fileType === 'image' ? (
                <img
                  src={pdfFile}
                  alt="Uploaded document"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    backgroundColor: COLORS.white,
                    pointerEvents: isDragging ? 'none' : 'auto'
                  }}
                />
              ) : (
                <iframe
                  src={`${pdfFile}#view=FitH&toolbar=1`}
                  style={{
                    width: "100%",
                    height: "100%",
                    border: "none",
                    backgroundColor: COLORS.white,
                    pointerEvents: isDragging ? 'none' : 'auto'
                  }}
                  title="Document Viewer"
                />
              )
            ) : (
              <div style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                color: COLORS.gray,
                fontSize: "14px"
              }}>
                No file uploaded
              </div>
            )}
          </div>
        </div>

        {/* Resizable Divider */}
        <div
          onMouseDown={handleMouseDown}
          style={{
            width: '8px',
            cursor: 'col-resize',
            backgroundColor: isDragging ? COLORS.accent : 'transparent',
            position: 'relative',
            flexShrink: 0,
            transition: isDragging ? 'none' : 'background-color 0.2s ease'
          }}
          onMouseEnter={(e) => {
            if (!isDragging) e.currentTarget.style.backgroundColor = COLORS.lightGray;
          }}
          onMouseLeave={(e) => {
            if (!isDragging) e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '3px',
            height: '40px',
            backgroundColor: COLORS.border,
            borderRadius: '2px'
          }} />
        </div>

        {/* Right Pane - Extracted Data / Text / Markdown */}
        <div style={{
          width: `${100 - splitPaneWidth}%`,
          minWidth: 0,
          minHeight: 0,
          boxSizing: "border-box",
          backgroundColor: COLORS.white,
          borderRadius: BORDER_RADIUS.lg,
          boxShadow: SHADOWS.lg,
          border: `1px solid ${COLORS.border}`,
          padding: SPACING.lg,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column"
        }}>
          <div style={{
            marginBottom: SPACING.md,
            position: "sticky",
            top: 0,
            backgroundColor: COLORS.white,
            paddingBottom: SPACING.md,
            borderBottom: `1px solid ${COLORS.borderLight}`,
            zIndex: 10
          }}>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: SPACING.sm, marginBottom: SPACING.sm }}>
              <div style={{ display: "flex", alignItems: "center", gap: SPACING.sm, flexWrap: "wrap" }}>
                <h3 className="mds-headline--x-small" style={{
                  margin: 0,
                  color: COLORS.text,
                }}>
                  Extraction
                </h3>
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  flexWrap: "wrap"
                }}>
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    padding: "4px 8px",
                    borderRadius: BORDER_RADIUS.md,
                    backgroundColor: COLORS.lightGray,
                    color: COLORS.text,
                    fontSize: "12px",
                    fontWeight: "500"
                  }} title="Text Extraction Time" aria-label="Text Extraction Time">
                    <span style={{ color: COLORS.textSecondary }}>TET</span>
                    <span style={{ color: COLORS.text, fontWeight: "600" }}>{formatServerTiming(serverTimings.extractionMs)}</span>
                  </div>
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    padding: "4px 8px",
                    borderRadius: BORDER_RADIUS.md,
                    backgroundColor: COLORS.lightGray,
                    color: COLORS.text,
                    fontSize: "12px",
                    fontWeight: "500"
                  }} title="Document Analysis Time" aria-label="Document Analysis Time">
                    <span style={{ color: COLORS.textSecondary }}>DAT</span>
                    <span style={{ color: COLORS.text, fontWeight: "600" }}>{formatServerTiming(serverTimings.analysisMs)}</span>
                  </div>
                  {showTokenUsage && (
                    <div style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      padding: "4px 8px",
                      borderRadius: BORDER_RADIUS.md,
                      backgroundColor: COLORS.lightGray,
                      color: COLORS.text,
                      fontSize: "12px",
                      fontWeight: "500",
                      whiteSpace: "nowrap"
                    }} title={tokenUsageTooltip} aria-label={tokenUsageTooltip}>
                      <span style={{ color: COLORS.textSecondary }}>Tokens</span>
                      <span style={{ color: COLORS.text, fontWeight: "600" }}>{formatTokenCount(tokenUsage.total_tokens)}</span>
                    </div>
                  )}
                </div>
              </div>
              
              {extractionView === 'data' && (
                <div style={{ display: "flex", alignItems: "center", gap: SPACING.sm, flexShrink: 0 }}>
                <McSegmentedControl
                  fit="small"
                  listchange={(e) => {
                    const next = e.detail?.item?.value;
                    if (next === 'table' || next === 'json') setDataDisplayMode(next);
                  }}
                >
                  <McSegmentedControlItem value="table" label="Table" icon="table" selected={dataDisplayMode === 'table'} />
                  <McSegmentedControlItem value="json" label="JSON" icon="code" selected={dataDisplayMode === 'json'} />
                </McSegmentedControl>
                <McButton
                  label={copiedJson ? 'Copied' : 'Copy JSON'}
                  title={keyValues && Object.keys(keyValues).length > 0 ? 'Copy JSON' : 'No data to copy'}
                  icon={copiedJson ? 'check' : 'copy'}
                  appearance="neutral"
                  variant="outlined"
                  fit="small"
                  disabled={!keyValues || Object.keys(keyValues).length === 0}
                  click={() => {
                    if (!keyValues || Object.keys(keyValues).length === 0) return;
                    navigator.clipboard.writeText(JSON.stringify(keyValues, null, 2));
                    setCopiedJson(true);
                    setTimeout(() => setCopiedJson(false), 2000);
                  }}
                  style={{ flexShrink: 0 }}
                />
                </div>
              )}
            </div>

            {/* View Mode Toggle */}
            <ViewTabs
              tabs={[
                { value: 'data', label: 'Extracted Data' },
                { value: 'text', label: 'Raw Text' },
                { value: 'markdown', label: 'Markdown' },
              ]}
              value={extractionView}
              onChange={setExtractionView}
            />
          </div>

          <div style={{ flex: 1, minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "column" }}>
            {extractionView === 'data' ? (
              keyValues && Object.keys(keyValues).length > 0 ? (
                <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
                  {dataDisplayMode === 'json' ? (
                    <pre style={{
                      margin: 0,
                      padding: SPACING.md,
                      backgroundColor: COLORS.lightGray,
                      borderRadius: BORDER_RADIUS.md,
                      color: COLORS.text,
                      fontFamily: "monospace",
                      fontSize: "12px",
                      lineHeight: 1.5,
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word"
                    }}>
                      {JSON.stringify(keyValues, null, 2)}
                    </pre>
                  ) : (
                    <JsonDisplay data={keyValues} />
                  )}
                </div>
              ) : (
                <div style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: SPACING.xl,
                  color: COLORS.gray,
                  textAlign: "center",
                  height: "400px"
                }}>
                  <McIcon icon="file" size="24" style={{ marginBottom: SPACING.md, opacity: 0.5 }} />
                  <p className="mds-text--small-normal" style={{ margin: 0 }}>No data extracted yet</p>
                  <p className="mds-text--x-small-normal" style={{ margin: `${SPACING.sm}px 0 0 0`, opacity: 0.7 }}>
                    {onlyOCR ? 'Parse a document to see results' : 'Upload and parse a document to see results'}
                  </p>
                </div>
              )
            ) : extractionView === 'markdown' ? (
              <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
                <DocumentTextarea
                  value={documentText}
                  placeholder="Upload a document to view its content here..."
                  readOnly={true}
                  renderMode="markdown"
                  style={{
                    height: "auto",
                    minHeight: "100%",
                    overflow: "visible"
                  }}
                />
              </div>
            ) : (
              <DocumentTextarea
                value={documentText}
                placeholder="Upload a document to view its content here..."
                readOnly={true}
                renderMode="text"
                style={{
                  flex: 1,
                  minHeight: 0,
                  height: "100%"
                }}
              />
            )}
          </div>

        </div>
      </div>

      {/* end right column */}
      </div>
      {/* end body flex (sidebar + content) */}
      </div>

      {/* Toast Notification */}
      <PlaygroundToast toast={toast} />

      {/* Model Settings Modal */}
      <McModal
        open={showModelSettings}
        heading="Model Settings"
        dimension="small"
        entercloseactiondisabled
        closed={() => setShowModelSettings(false)}
      >
        {showModelSettings && (PLAYGROUND_REASONING_MODELS.includes(selectedModel) ? (
          <>
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: SPACING.sm,
            }}>
              <label htmlFor="playground-reasoning-effort" className="mds-text--small-bold" style={{ color: COLORS.text }}>
                Reasoning Effort
              </label>
              <span className="mds-text--small-bold" style={{
                color: COLORS.text,
                minWidth: "60px",
                textAlign: "right",
                textTransform: "capitalize",
              }}>
                {reasoningEffort}
              </span>
            </div>

            <div className="mds-text--x-small-normal" style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: SPACING.xs,
              color: COLORS.textSecondary,
            }}>
              <span>Low</span>
              <span>High</span>
            </div>

            <div style={rangeWrapperStyle}>
              {/* Filled track background */}
              <div style={{ ...rangeTrackStyle, backgroundColor: COLORS.primary, width: `${(["low", "medium", "high"].indexOf(reasoningEffort) / 2) * 100}%`, zIndex: 1 }} />

              {/* Unfilled track background */}
              <div style={{ ...rangeTrackStyle, backgroundColor: COLORS.border, width: "100%", zIndex: 0 }} />

              {/* Native range input: MDS has no slider component */}
              <input
                id="playground-reasoning-effort"
                className="playground-range"
                type="range"
                min="0"
                max="2"
                step="1"
                value={["low", "medium", "high"].indexOf(reasoningEffort)}
                onChange={(e) => {
                  const efforts = ["low", "medium", "high"];
                  const val = efforts[parseInt(e.target.value)];
                  setReasoningEffort(val);
                  sessionStorage.setItem("playground_reasoningEffort", val);
                }}
                style={rangeInputStyle}
              />
            </div>
          </>
        ) : PLAYGROUND_FIXED_SETTINGS_MODELS.includes(selectedModel) ? (
          <p className="mds-text--small-normal" style={{ margin: `0 0 ${SPACING.xl}px 0`, color: COLORS.textSecondary, lineHeight: 1.6 }}>
            {selectedModel} runs with its default settings — it doesn&apos;t accept a temperature or reasoning effort here.
          </p>
        ) : (
          <>
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: SPACING.sm,
            }}>
              <label htmlFor="playground-temperature" className="mds-text--small-bold" style={{ color: COLORS.text }}>
                Temperature
              </label>
              <span className="mds-text--medium-bold" style={{
                color: COLORS.text,
                minWidth: "40px",
                textAlign: "right",
              }}>
                {temperature.toFixed(1)}
              </span>
            </div>

            <div className="mds-text--x-small-normal" style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: SPACING.xs,
              color: COLORS.textSecondary,
            }}>
              <span>Deterministic</span>
              <span>Creative</span>
            </div>

            <div style={rangeWrapperStyle}>
              {/* Filled track background */}
              <div style={{ ...rangeTrackStyle, backgroundColor: COLORS.primary, width: `${temperature * 100}%`, zIndex: 1 }} />

              {/* Unfilled track background */}
              <div style={{ ...rangeTrackStyle, backgroundColor: COLORS.border, width: "100%", zIndex: 0 }} />

              {/* Native range input: MDS has no slider component */}
              <input
                id="playground-temperature"
                className="playground-range"
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={temperature}
                onChange={(e) => { const v = parseFloat(e.target.value); setTemperature(v); sessionStorage.setItem("playground_temperature", v); }}
                style={rangeInputStyle}
              />
            </div>
          </>
        ))}

        <McButton
          slot="primaryAction"
          label="Done"
          appearance="primary"
          variant="filled"
          click={() => setShowModelSettings(false)}
        />
      </McModal>

      <style>
        {`
          @keyframes slideIn {
            from {
              transform: translateX(100%);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }

          /* Range slider (Model Settings) — MDS tokens only */
          input.playground-range {
            -webkit-appearance: none;
            appearance: none;
            width: 100%;
            background: transparent;
            cursor: pointer;
            height: 20px;
            outline: none;
            padding: 0;
            margin: 0;
            border: none;
          }

          input.playground-range:focus-visible::-webkit-slider-thumb {
            outline: 2px solid var(--mds_brand_appearance_primary_default_border-color);
            outline-offset: 2px;
          }

          input.playground-range::-webkit-slider-runnable-track {
            background: transparent;
            height: 4px;
            border-radius: 2px;
            border: none;
          }

          input.playground-range::-webkit-slider-thumb {
            -webkit-appearance: none;
            appearance: none;
            background: var(--mds_brand_appearance_primary_default_background-color);
            height: 18px;
            width: 18px;
            margin-top: -7px;
            border-radius: 50%;
            cursor: pointer;
            border: 2px solid var(--mds_brand_appearance_neutral_default_background-color);
            box-shadow: ${SHADOWS.sm};
          }

          input.playground-range:hover::-webkit-slider-thumb {
            box-shadow: ${SHADOWS.lg};
          }

          input.playground-range::-moz-range-track {
            background: transparent;
            height: 4px;
            border-radius: 2px;
            border: none;
          }

          input.playground-range::-moz-range-thumb {
            background: var(--mds_brand_appearance_primary_default_background-color);
            height: 18px;
            width: 18px;
            border-radius: 50%;
            cursor: pointer;
            border: 2px solid var(--mds_brand_appearance_neutral_default_background-color);
            box-shadow: ${SHADOWS.sm};
          }

          input.playground-range:hover::-moz-range-thumb {
            box-shadow: ${SHADOWS.lg};
          }

          input.playground-range::-moz-focus-outer {
            border: 0;
          }
        `}
      </style>
    </div>
  );
}
